/**
 * O desenho do Escritório (SPEC-20261009-2000, SPEC-20261010-0013): um andar
 * de escritório em pixel art, visto de frente.
 *
 * Tudo é pintado pixel a pixel num buffer próprio (sem suavização), na
 * resolução baixa do tabuleiro, e a tela só amplia. Cada objeto é uma caixa
 * com textura nas três faces; a ordem de pintura vem de uma ordenação por
 * profundidade entre as caixas. O que se mexe (água, aquário, telas, vapor)
 * é desenhado por cima, a cada quadro, sem repintar o resto.
 */

export type Pt = [number, number]
type Tex = (x: number, y: number, z: number) => number
type Caixa = { b: [number, number, number, number, number]; f: () => void }
type Alvo = { W: number; H: number; OX: number; OY: number; img: ImageData; px: Uint32Array }
type Opcoes = { c?: string; top?: Tex; l?: Tex; r?: Tex; o?: string; noTop?: boolean; layered?: boolean }

export type ModeloId = 'reuniao' | 'time' | 'estudio' | 'copa' | 'chefia' | 'cabine'
export type SalaNoMapa = {
  id: string
  lot: number
  template: ModeloId
  trancada: boolean
  podeEntrar: boolean
  /** Nome escrito na placa da sala, em pixel art, e quantas pessoas estão lá. */
  nome?: string
  pessoas?: number
  /** Cor da equipe dona da sala (SPEC-20261010-1557): faixa na placa. */
  cor?: string | null
}
type Lote = { id: number; x0: number; y0: number; x1: number; y1: number }

/* ---------------------------------------------------------------- pintura --- */

let T: Alvo
const novoAlvo = (W: number, H: number, OX: number, OY: number): Alvo => {
  const img = new ImageData(W, H)
  return { W, H, OX, OY, img, px: new Uint32Array(img.data.buffer) }
}
const C = (hex: string) => {
  const n = parseInt(hex.slice(1), 16)
  return (255 << 24) | ((n & 255) << 16) | (((n >> 8) & 255) << 8) | (n >> 16)
}
// vista de frente e reta (SPEC-20261010-0013): x anda para a direita, o fundo sobe, a altura sobe
const TX = 16
const TY = 13.5
const pr = (x: number, y: number, z = 0): Pt => [T.OX + x * TX, T.OY + y * TY - z]
const hash = (x: number, y: number, s = 0) => {
  let h = (x * 374761393 + y * 668265263 + s * 982451653) | 0
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}
const set = (x: number, y: number, c: number) => {
  if (x >= 0 && y >= 0 && x < T.W && y < T.H) T.px[y * T.W + x] = c
}
function escurecer(x: number, y: number, a: number) {
  if (x < 0 || y < 0 || x >= T.W || y >= T.H) return
  const i = y * T.W + x
  const v = T.px[i] ?? 0
  if (!(v >>> 24)) return
  const r = v & 255
  const g = (v >> 8) & 255
  const b = (v >> 16) & 255
  T.px[i] = (255 << 24) | (Math.round(b * (1 - a)) << 16) | (Math.round(g * (1 - a)) << 8) | Math.round(r * (1 - a))
}
export function tom(hex: string, k: number) {
  const n = parseInt(hex.slice(1), 16)
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(k < 0 ? v * (1 + k) : v + (255 - v) * k)))
  return '#' + ((1 << 24) | (f(n >> 16) << 16) | (f((n >> 8) & 255) << 8) | f(n & 255)).toString(16).slice(1)
}
const liso = (hex: string): Tex => {
  const c = C(hex)
  return () => c
}
const ruido = (base: string, vars: string[], d = 0.22, s = 1): Tex => {
  const b = C(base)
  const v = vars.map(C)
  return (x, y) => {
    const h = hash(x, y, s)
    return h < d ? (v[Math.floor((h / d) * v.length)] ?? b) : b
  }
}
const tijolo = (base: string, massa: string, claro: string): Tex => {
  const b = C(base)
  const m = C(massa)
  const v = C(claro)
  return (x, y) => {
    const linha = Math.floor(y / 4)
    if (y % 4 === 0 || (x + (linha % 2) * 4) % 8 === 0) return m
    return hash(x, y, 7) < 0.2 ? v : b
  }
}
const tabuas = (a: string, b2: string, fresta: string): Tex => {
  const A = C(a)
  const B = C(b2)
  const G = C(fresta)
  return (x, y) => {
    const k = Math.floor((x + 2 * y) / 6)
    if ((x + 2 * y) % 6 === 0) return G
    return hash(k, 0, 3) < 0.5 ? A : B
  }
}
function poligono(pts: Pt[], tex: Tex, zfn?: ((x: number, y: number) => number) | null) {
  let minY = 1e9
  let maxY = -1e9
  for (const p of pts) {
    minY = Math.min(minY, p[1])
    maxY = Math.max(maxY, p[1])
  }
  for (let y = Math.floor(minY); y < Math.ceil(maxY); y++) {
    const sy = y + 0.5
    const xs: number[] = []
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i] as Pt
      const c = pts[(i + 1) % pts.length] as Pt
      if ((a[1] <= sy && c[1] > sy) || (c[1] <= sy && a[1] > sy)) xs.push(a[0] + ((sy - a[1]) * (c[0] - a[0])) / (c[1] - a[1]))
    }
    xs.sort((m, n) => m - n)
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const x0 = Math.round(xs[k] as number)
      const x1 = Math.round(xs[k + 1] as number)
      for (let x = x0; x < x1; x++) set(x, y, tex(x, y, zfn ? zfn(x, y) : 0))
    }
  }
}
function sombra(pts: Pt[], a: number) {
  let minY = 1e9
  let maxY = -1e9
  for (const p of pts) {
    minY = Math.min(minY, p[1])
    maxY = Math.max(maxY, p[1])
  }
  for (let y = Math.floor(minY); y < Math.ceil(maxY); y++) {
    const sy = y + 0.5
    const xs: number[] = []
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i] as Pt
      const q = pts[(i + 1) % pts.length] as Pt
      if ((p[1] <= sy && q[1] > sy) || (q[1] <= sy && p[1] > sy)) xs.push(p[0] + ((sy - p[1]) * (q[0] - p[0])) / (q[1] - p[1]))
    }
    xs.sort((m, n) => m - n)
    for (let k = 0; k + 1 < xs.length; k += 2)
      for (let x = Math.round(xs[k] as number); x < Math.round(xs[k + 1] as number); x++) escurecer(x, y, a)
  }
}
function linha(a: Pt, b: Pt, cor: string | number) {
  const c = typeof cor === 'number' ? cor : C(cor)
  let x0 = Math.round(a[0])
  let y0 = Math.round(a[1])
  const x1 = Math.round(b[0])
  const y1 = Math.round(b[1])
  const dx = Math.abs(x1 - x0)
  const dy = -Math.abs(y1 - y0)
  const sx = x0 < x1 ? 1 : -1
  const sy = y0 < y1 ? 1 : -1
  let e = dx + dy
  for (;;) {
    set(x0, y0, c)
    if (x0 === x1 && y0 === y1) break
    const e2 = 2 * e
    if (e2 >= dy) {
      e += dy
      x0 += sx
    }
    if (e2 <= dx) {
      e += dx
      y0 += sy
    }
  }
}
function caixa(x: number, y: number, z: number, w: number, d: number, h: number, o: Opcoes) {
  const cor = o.c ?? '#888888'
  const topo = o.top ?? ruido(tom(cor, 0.2), [tom(cor, 0.32), tom(cor, 0.08)], 0.18, 2)
  const esq = o.l ?? ruido(cor, [tom(cor, -0.08), tom(cor, 0.06)], 0.18, 4)
  const dir = o.r ?? ruido(tom(cor, -0.24), [tom(cor, -0.32), tom(cor, -0.16)], 0.18, 5)
  const contorno = C(o.o ?? tom(cor, -0.6))
  const fl: Pt[] = [pr(x, y + d, z), pr(x + w, y + d, z), pr(x + w, y + d, z + h), pr(x, y + d, z + h)]
  const fr: Pt[] = [pr(x + w, y, z), pr(x + w, y + d, z), pr(x + w, y + d, z + h), pr(x + w, y, z + h)]
  const tp: Pt[] = [pr(x, y, z + h), pr(x + w, y, z + h), pr(x + w, y + d, z + h), pr(x, y + d, z + h)]
  if (h > 0) {
    const zL = o.layered
      ? (X: number, Y: number) => {
          const b = pr(x, y + d, 0)
          return b[1] + (X - b[0]) / 2 - Y - (z + h)
        }
      : null
    const zR = o.layered
      ? (X: number, Y: number) => {
          const b = pr(x + w, y, 0)
          return b[1] - (X - b[0]) / 2 - Y - (z + h)
        }
      : null
    poligono(fl, esq, zL)
    poligono(fr, dir, zR)
  }
  if (!o.noTop) poligono(tp, topo)
  for (let i = 0; i < 4; i++) linha(tp[i] as Pt, tp[(i + 1) % 4] as Pt, contorno)
  if (h > 0) {
    linha(pr(x, y + d, z), pr(x + w, y + d, z), contorno)
    linha(pr(x + w, y, z), pr(x + w, y + d, z), contorno)
    linha(pr(x, y + d, z), pr(x, y + d, z + h), contorno)
    linha(pr(x + w, y + d, z), pr(x + w, y + d, z + h), contorno)
    linha(pr(x + w, y, z), pr(x + w, y, z + h), contorno)
  }
}
const texOu = (t: Tex | string) => (typeof t === 'string' ? liso(t) : t)
const paredeY = (y: number, u0: number, u1: number, z0: number, z1: number, t: Tex | string) =>
  poligono([pr(u0, y, z0), pr(u1, y, z0), pr(u1, y, z1), pr(u0, y, z1)], texOu(t))
const paredeX = (x: number, u0: number, u1: number, z0: number, z1: number, t: Tex | string) =>
  poligono([pr(x, u0, z0), pr(x, u1, z0), pr(x, u1, z1), pr(x, u0, z1)], texOu(t))
const chao = (x0: number, x1: number, y0: number, y1: number, z: number, t: Tex | string) =>
  poligono([pr(x0, y0, z), pr(x1, y0, z), pr(x1, y1, z), pr(x0, y1, z)], texOu(t))
function piso(x0: number, x1: number, y0: number, y1: number, z: number, a: string, b: string, s = 1) {
  for (let x = x0; x < x1; x++)
    for (let y = y0; y < y1; y++) {
      const c = (Math.floor(x) + Math.floor(y)) & 1 ? a : b
      chao(x, Math.min(x + 1, x1), y, Math.min(y + 1, y1), z, ruido(c, [tom(c, 0.07), tom(c, -0.06)], 0.2, Math.round(s + x * 3 + y)))
    }
}
/** Atrás antes da frente: ordenação topológica entre as caixas que se sobrepõem na tela. */
function ordenar(lista: Caixa[]): Caixa[] {
  const n = lista.length
  const soma = (c: Caixa) => c.b[0] + c.b[1] + c.b[2] + c.b[3]
  const antes = (A: Caixa, B: Caixa) => {
    const a = A.b
    const b = B.b
    const ab = a[1] <= b[0] + 1e-6 || a[3] <= b[2] + 1e-6
    const ba = b[1] <= a[0] + 1e-6 || b[3] <= a[2] + 1e-6
    if (ab && !ba) return true
    if (ba && !ab) return false
    if (!ab && !ba && a[4] !== b[4]) return a[4] < b[4]
    return soma(A) < soma(B)
  }
  const grau = new Array<number>(n).fill(0)
  const adj: number[][] = lista.map(() => [])
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++) {
      if (i === j) continue
      const a = (lista[i] as Caixa).b
      const b = (lista[j] as Caixa).b
      if (a[1] - a[2] < b[0] - b[3] - 1 || b[1] - b[2] < a[0] - a[3] - 1) continue
      if (antes(lista[i] as Caixa, lista[j] as Caixa)) {
        ;(adj[i] as number[]).push(j)
        grau[j] = (grau[j] ?? 0) + 1
      }
    }
  const saida: Caixa[] = []
  const feito = new Array<boolean>(n).fill(false)
  for (let k = 0; k < n; k++) {
    let p = -1
    for (let i = 0; i < n; i++)
      if (!feito[i] && (grau[i] ?? 0) <= 0 && (p < 0 || soma(lista[i] as Caixa) < soma(lista[p] as Caixa))) p = i
    if (p < 0)
      for (let i = 0; i < n; i++) if (!feito[i] && (p < 0 || soma(lista[i] as Caixa) < soma(lista[p] as Caixa))) p = i
    feito[p] = true
    saida.push(lista[p] as Caixa)
    for (const j of adj[p] as number[]) grau[j] = (grau[j] ?? 0) - 1
  }
  return saida
}


/* ---------------------------------------------------------------- modelos --- */

type F = (x: number, y: number, w: number, d: number, z: number, h: number, o: Opcoes) => void
type Parede = { y: (u0: number, u1: number, z0: number, z1: number, t: Tex | string) => void; x: (u0: number, u1: number, z0: number, z1: number, t: Tex | string) => void }
type Modelo = {
  nome: string
  dica: string
  h: number
  piso: [string, string]
  parede: string
  cor: string
  assentos: Pt[]
  montar: (F: F) => void
  enfeitar: (P: Parede, h: number) => void
  anim: 'tv' | 'codigo' | 'luminaria' | 'copa' | null
}
const planta = (F: F, x: number, y: number, z0 = 0, s = 1) => {
  F(x, y, 0.7 * s, 0.7 * s, z0, 6, { c: '#b46a3c' })
  F(x - 0.1, y - 0.1, 0.9 * s, 0.9 * s, z0 + 6, 6 * s, { c: '#3e9a55', top: ruido('#56b86b', ['#6fd283', '#3e9a55'], 0.35, Math.round(x * 9)) })
}

export const MODELOS: Record<ModeloId, Modelo> = {
  reuniao: {
    nome: 'Sala de reunião',
    dica: 'Mesa grande e TV',
    h: 14,
    piso: ['#4561cf', '#3d58c4'],
    parede: '#f3ede2',
    cor: '#3f5bc9',
    assentos: [[1.6, 1.4], [3, 1.4], [4.4, 1.4], [1.6, 4.6], [3, 4.6], [4.4, 4.6], [0.5, 3], [5.4, 3]],
    montar(F) {
      F(1.1, 2.2, 3.8, 1.6, 0, 7, { c: '#7a4f2c' })
      F(0.95, 2.05, 4.1, 1.9, 7, 2, { c: '#a8744a', top: tabuas('#c08a5a', '#b47f50', '#8c5a34') })
      F(2.4, 2.7, 0.9, 0.5, 9, 1, { c: '#eef2f8' })
      F(3.7, 2.6, 0.35, 0.35, 9, 3, { c: '#bfe8fb', top: liso('#eaf8ff') })
      F(1.4, 2.5, 0.35, 0.35, 9, 3, { c: '#bfe8fb', top: liso('#eaf8ff') })
      for (const x of [1.3, 2.7, 4.1]) {
        F(x, 1.1, 0.7, 0.7, 0, 6, { c: '#2b3157' })
        F(x, 1.1, 0.7, 0.18, 6, 7, { c: '#3a4275' })
        F(x, 4.2, 0.7, 0.7, 0, 6, { c: '#2b3157' })
        F(x, 4.72, 0.7, 0.18, 6, 7, { c: '#3a4275' })
      }
      F(0.2, 2.65, 0.7, 0.7, 0, 6, { c: '#2b3157' })
      F(5.1, 2.65, 0.7, 0.7, 0, 6, { c: '#2b3157' })
      planta(F, 5.1, 0.2)
      F(0.2, 5.2, 0.6, 0.6, 0, 9, { c: '#9aa1ad' })
      F(0.15, 5.15, 0.7, 0.7, 9, 4, { c: '#5fb3e3', top: liso('#bfe8fb') })
    },
    enfeitar(P, h) {
      P.y(1.8, 4.8, h + 8, h + 22, '#20253d')
      P.y(1.95, 4.65, h + 9, h + 21, '#2d3a7a')
      P.x(1.2, 4.2, h + 9, h + 20, '#c9c2b4')
      P.x(1.35, 4.05, h + 10, h + 19, '#ffffff')
      P.x(1.6, 2.1, h + 16, h + 17, '#3f6fe0')
      P.x(2.4, 2.9, h + 13, h + 14, '#e5484d')
      P.x(3.2, 3.7, h + 15, h + 16, '#2fb36d')
    },
    anim: 'tv',
  },
  time: {
    nome: 'Sala do time',
    dica: 'Mesas com computadores',
    h: 10,
    piso: ['#555f78', '#4c556c'],
    parede: '#d9dfe9',
    cor: '#4d566c',
    assentos: [[1.55, 2.25], [4.55, 2.25], [1.55, 5.15], [4.55, 5.15]],
    montar(F) {
      for (const [x, y] of [[0.4, 0.6], [3.4, 0.6], [0.4, 3.5], [3.4, 3.5]] as Pt[]) {
        F(x, y, 2.2, 1, 0, 9, { c: '#c9cfdb', top: ruido('#e7ebf2', ['#f2f5f9'], 0.2, 9) })
        F(x + 0.7, y + 0.15, 0.9, 0.15, 9, 8, { c: '#1a1f33' })
        F(x + 0.3, y + 0.65, 0.6, 0.25, 9, 1, { c: '#2a2f44' })
        F(x + 1.75, y + 0.4, 0.22, 0.22, 9, 2, { c: '#ffffff', top: liso('#7a4a24') })
        F(x + 0.75, y + 1.25, 0.8, 0.8, 0, 6, { c: '#1f2440' })
        F(x + 0.75, y + 1.95, 0.8, 0.15, 6, 6, { c: '#2a3057' })
      }
      F(5.2, 5.1, 0.7, 0.7, 0, 18, { c: '#2a2f44', top: liso('#3a4160') })
      planta(F, 0.2, 5.3)
    },
    enfeitar(P, h) {
      P.y(1.2, 2.6, h + 12, h + 20, '#1a2140')
      P.y(1.3, 2.5, h + 13, h + 19, ruido('#2f4fd8', ['#ffb020'], 0.15, 4))
      P.x(2, 3.6, h + 11, h + 18, '#f6f3ec')
      for (let k = 0; k < 4; k++) P.x(2.2 + k * 0.35, 2.4 + k * 0.35, h + 12, h + 14 + k, '#2fb36d')
    },
    anim: 'codigo',
  },
  estudio: {
    nome: 'Estúdio criativo',
    dica: 'Bancada, quadro e pufes',
    h: 10,
    piso: ['#ecb089', '#e3a57f'],
    parede: '#fbe3d6',
    cor: '#e1a088',
    assentos: [[2.6, 2.4], [4.85, 4.65], [1.6, 4.8], [3.15, 4.3]],
    montar(F) {
      F(1.6, 0.8, 3, 1.2, 0, 9, { c: '#f1e3c8', top: ruido('#fff4dc', ['#ffe9c2'], 0.2, 4) })
      F(3, 1, 0.6, 0.4, 9, 4, { c: '#e0679b' })
      F(2, 1, 0.9, 0.8, 9, 1, { c: '#ffffff' })
      F(4.1, 0.95, 0.25, 0.25, 9, 6, { c: '#3a3f55' })
      F(3.95, 0.8, 0.55, 0.55, 15, 2, { c: '#fff3c4', top: liso('#ffffff') })
      F(4.2, 4, 1.3, 1.3, 0, 5, { c: '#ffb020', top: ruido('#ffc955', ['#ffd77a'], 0.3, 3) })
      F(1, 4.2, 1.2, 1.2, 0, 5, { c: '#3f6fe0', top: ruido('#6b91f0', ['#86a6f5'], 0.3, 2) })
      F(2.6, 3.8, 1.1, 1.1, 0, 1, { c: '#ffd34d', top: ruido('#ffe28a', ['#ff8fa3', '#7fd4ff'], 0.4, 6) })
      F(5.3, 0.3, 0.5, 0.5, 0, 14, { c: '#3a3f55' })
      F(5.1, 0.15, 0.9, 0.5, 14, 5, { c: '#2a2f44' })
    },
    enfeitar(P, h) {
      P.y(1.5, 4.7, h + 8, h + 21, '#7a5c3c')
      P.y(1.7, 4.5, h + 9, h + 20, '#f6f3ec')
      for (const [c, u, z] of [['#ffd34d', 1.9, 15], ['#ff8fa3', 2.8, 17], ['#7fd4ff', 3.7, 14], ['#b6f08a', 2.2, 11], ['#ffd34d', 3.9, 10], ['#ff8fa3', 3.2, 12]] as const)
        P.y(u, u + 0.45, h + z, h + z + 3, c)
      for (let z = 4; z < 22; z += 6) P.x(3.5, 5.6, h + z, h + z + 1, '#6f5236')
      ;(['#e0679b', '#3f6fe0', '#ffb020', '#1f9e8a'] as const).forEach((c, k) => P.x(3.7 + k * 0.45, 4 + k * 0.45, h + 5 + (k % 3) * 6, h + 9 + (k % 3) * 6, c))
    },
    anim: 'luminaria',
  },
  copa: {
    nome: 'Copa',
    dica: 'Aquário, café e sofá',
    h: 6,
    piso: ['#a7dcc8', '#9bd2bd'],
    parede: '#e3f4ec',
    cor: '#97cfba',
    assentos: [[2.4, 5.2], [3.6, 5.2], [4.8, 5.2], [3.6, 3.3]],
    montar(F) {
      F(0.4, 0.4, 3, 1, 0, 8, { c: '#5a3a24', top: liso('#7a5236') })
      F(0.4, 0.4, 3, 1, 8, 15, { c: '#7cc3e8', l: liso('#5fb3e3'), r: liso('#4a9fd0'), top: liso('#bfe8fb'), o: '#2b6a8f' })
      F(1.7, 4.9, 3.8, 0.9, 0, 5, { c: '#e0679b', top: ruido('#ee86b2', ['#f59ac0'], 0.2, 2) })
      F(1.7, 5.55, 3.8, 0.4, 5, 6, { c: '#c95486' })
      F(1.45, 4.9, 0.3, 1, 0, 8, { c: '#c95486' })
      F(5.5, 4.9, 0.3, 1, 0, 8, { c: '#c95486' })
      F(2.9, 2.8, 1.4, 1, 0, 5, { c: '#6b4a2e', top: tabuas('#8c6440', '#7f5a39', '#5a3a24') })
      F(3.3, 3.1, 0.3, 0.3, 5, 2, { c: '#ffffff' })
      F(4, 0.3, 1.8, 1, 0, 10, { c: '#c9cfdb', top: liso('#eef1f6') })
      F(4.5, 0.4, 0.8, 0.6, 10, 7, { c: '#2a2f44' })
      F(4.75, 0.75, 0.3, 0.3, 10, 1.5, { c: '#ffffff' })
      F(5.2, 2, 0.75, 0.75, 0, 16, { c: '#e6ebf2', top: liso('#ffffff') })
    },
    enfeitar(P, h) {
      P.x(2, 3.6, h + 12, h + 20, '#7a5c3c')
      P.x(2.15, 3.45, h + 13, h + 19, ruido('#7fd4ff', ['#ffd34d', '#b6f08a'], 0.3, 9))
      P.y(4, 5.8, h + 20, h + 22, '#2f4fd8')
    },
    anim: 'copa',
  },
  chefia: {
    nome: 'Sala da chefia',
    dica: 'Mesa, poltronas e estante',
    h: 18,
    piso: ['#317058', '#2b6650'],
    parede: '#ece3cf',
    cor: '#2a644f',
    assentos: [[2.45, 4.6], [4.25, 4.6], [3, 0.7]],
    montar(F) {
      F(1.2, 3.6, 4, 2.2, 0, 0.3, { c: '#7a2e3a', top: ruido('#9a4050', ['#c25a6c', '#7a2e3a'], 0.3, 4) })
      F(1.5, 1.1, 3, 1.2, 0, 9, { c: '#3b2a1f', top: tabuas('#5a3f2c', '#513826', '#2e1f15') })
      F(2.3, 1.25, 1.2, 0.15, 9, 7, { c: '#1a1f33' })
      F(4, 1.4, 0.4, 0.4, 9, 3, { c: '#2f4fd8', top: liso('#6b91f0') })
      for (const [x, y] of [[1.9, 4.2], [3.7, 4.2]] as Pt[]) {
        F(x, y, 1.1, 1.1, 0, 5, { c: '#7a2e3a' })
        F(x, y + 0.8, 1.1, 0.3, 5, 6, { c: '#6a2531' })
      }
      F(5.2, 4.8, 0.6, 0.6, 0, 5, { c: '#c9a26a' })
      F(5.25, 4.85, 0.5, 0.5, 5, 5, { c: '#49b2e6', top: ruido('#4caf3e', ['#49b2e6'], 0.5, 3) })
      planta(F, 5, 0.2)
    },
    enfeitar(P, h) {
      for (let z = 4; z < 22; z += 5) P.x(0.6, 3.4, h + z, h + z + 1, '#6f5236')
      ;(['#e0679b', '#3f6fe0', '#ffb020', '#1f9e8a', '#8a5be0'] as const).forEach((c, k) => P.x(0.8 + k * 0.45, 1.15 + k * 0.45, h + 5 + (k % 3) * 5, h + 9 + (k % 3) * 5, c))
      P.y(2, 4, h + 12, h + 20, '#c9a26a')
      P.y(2.15, 3.85, h + 13, h + 19, ruido('#7fb8d9', ['#a7d3ea'], 0.2, 2))
    },
    anim: null,
  },
  cabine: {
    nome: 'Sala 1:1',
    dica: 'Duas poltronas, conversa a dois',
    h: 8,
    piso: ['#c6a6e8', '#bc9be0'],
    parede: '#f1eaf9',
    cor: '#a782d6',
    assentos: [[2.25, 3.4], [4.15, 3.4]],
    montar(F) {
      F(1.6, 2.4, 3, 2, 0, 0.3, { c: '#ffd34d', top: ruido('#ffe28a', ['#ffb020'], 0.3, 2) })
      F(1.7, 2.9, 1.1, 1.1, 0, 5, { c: '#3f6fe0' })
      F(1.7, 2.9, 1.1, 0.3, 5, 6, { c: '#2f4fd8' })
      F(3.6, 2.9, 1.1, 1.1, 0, 5, { c: '#e0679b' })
      F(3.6, 2.9, 1.1, 0.3, 5, 6, { c: '#c95486' })
      F(2.95, 3.1, 0.6, 0.6, 0, 5, { c: '#6b4a2e' })
      F(0.4, 0.4, 0.3, 0.3, 0, 16, { c: '#3a3f55' })
      F(0.2, 0.2, 0.7, 0.7, 16, 3, { c: '#fff3c4', top: liso('#ffffff') })
      planta(F, 5, 4.8)
    },
    enfeitar(P, h) {
      P.y(2.4, 3.6, h + 10, h + 19, '#8c6a49')
      P.y(2.5, 3.5, h + 11, h + 18, ruido('#ffb020', ['#ff8fa3'], 0.3, 5))
    },
    anim: null,
  },
}

/* ------------------------------------------------------------- o andar --- */

/**
 * Tabuleiros prontos (SPEC-20261010-0013, SPEC-20261010-0350): andares de
 * escritório vistos de frente, retos, na proporção da aba. Cada tabuleiro tem
 * vagas de sala em lugar fixo; o modelo de desenho e o nome vêm da sala gravada
 * naquela vaga (lote). A vaga 0 é a área central de cada tabuleiro, sem caixa.
 */
export type Vaga = Lote & {
  fileira: 'cima' | 'baixo'
  /** Planta livre (SPEC-20261011-2023): os quadradinhos da sala (y * w + x) e a largura da planta. */
  celulas?: Set<number>
  w?: number
  /** Planta livre: lugares de sentar da sala (dos móveis dentro dela ou o meio dos quadradinhos). */
  assentos?: Pt[]
}
export type Retangulo = { x0: number; x1: number; y0: number; y1: number }
/** Sala de 6 x 6: em cima encosta na parede de janelas; embaixo, na fachada. */
const vaga = (id: number, x0: number, y0: number, fileira: Vaga['fileira']): Vaga => ({ id, x0, y0, x1: x0 + 6, y1: y0 + 6, fileira })

const PISO: Tex = (() => {
  const A = C('#d3a46c')
  const B = C('#c99a63')
  const G = C('#a8794a')
  return (x, y) => {
    const fila = Math.floor(y / 5)
    const desvio = Math.floor(hash(fila, 0, 5) * 24)
    if (y % 5 === 0 || (x + desvio) % 26 === 0) return G
    const c = (Math.floor((x + desvio) / 26) + fila) % 2 ? A : B
    return hash(x, y, 2) < 0.08 ? G : c
  }
})()
const TABUA_ESCURA = tabuas('#a8744a', '#9a6a42', '#6b4527')

/** Vidro: clareia o que já foi pintado atrás, com reflexo e caixilho. */
const CAIXILHO = C('#8fa6ba')
function clarear(sx: number, sy: number, k: number) {
  if (sx < 0 || sy < 0 || sx >= T.W || sy >= T.H) return
  const i = sy * T.W + sx
  const v = T.px[i] ?? 0
  if (!(v >>> 24)) return
  const r = v & 255
  const g = (v >> 8) & 255
  const b = (v >> 16) & 255
  T.px[i] = (255 << 24) | (Math.round(b + (248 - b) * k) << 16) | (Math.round(g + (240 - g) * k) << 8) | Math.round(r + (222 - r) * k)
}
function vidro(y: number, u0: number, u1: number, z0: number, z1: number, passo = 24) {
  const a = pr(u0, y, z1)
  const b = pr(u1, y, z0)
  const X0 = Math.round(a[0])
  const X1 = Math.round(b[0])
  const Y0 = Math.round(a[1])
  const Y1 = Math.round(b[1])
  for (let sy = Y0; sy <= Y1; sy++)
    for (let sx = X0; sx <= X1; sx++) {
      if (sy === Y0 || sy === Y1 || sx === X0 || sx === X1 || (sx - X0) % passo === 0) set(sx, sy, CAIXILHO)
      else {
        const d = (((sx - sy) % 37) + 37) % 37
        clarear(sx, sy, d < 2 ? 0.55 : d < 4 ? 0.4 : 0.22)
      }
    }
}

export type Add = (b: Caixa['b'], f: () => void) => void

function itensDaSala(add: Add, modeloId: ModeloId, v: Vaga, vizinhaADireita: boolean) {
  const m = MODELOS[modeloId]
  const { x0, y0, x1, y1 } = v
  const embaixo = v.fileira === 'baixo'
  const d = y1 - y0
  const papel = ruido(m.parede, [tom(m.parede, -0.05), tom(m.parede, 0.05)], 0.25, Math.round(x0 * 7))
  const P0 = x0 + 2.2
  const P1 = x0 + 3.8
  const batente = (y: number, z: number) => {
    caixa(P0 - 0.12, y, 0, 0.12, 0.3, z, { c: '#6b5440' })
    caixa(P1, y, 0, 0.12, 0.3, z, { c: '#6b5440' })
  }
  const P: Parede = {
    y: (u0, u1, z0, z1, t) => paredeY(y0 - 0.3, x0 + u0, x0 + u1, z0, z1, t),
    x: (u0, u1, z0, z1, t) => paredeX(x0 - 0.3, y0 + u0, y0 + u1, z0, z1, t),
  }
  const nada = () => {}
  // parede do fundo: em cima é a parede de janelas do prédio; embaixo, vidro com a porta
  if (!embaixo) {
    add([x0 - 0.3, x1 + 0.3, y0 - 0.3, y0, 0], () => {
      caixa(x0 - 0.3, y0 - 0.3, 0, 6.6, 0.3, 30, { c: m.parede, l: papel, top: liso('#5a4128'), o: '#5a4a38' })
      paredeY(y0, x0, x1, 0, 2, tom(m.parede, -0.2))
      for (const u of [x0 + 0.4, x0 + 4.2]) {
        paredeY(y0 - 0.3, u, u + 1.4, 11, 26, '#7f95ad')
        paredeY(y0 - 0.3, u + 0.12, u + 1.28, 12, 25, ruido('#bfe3f5', ['#d9f0fb'], 0.2, Math.round(u * 7)))
        paredeY(y0 - 0.3, u + 0.66, u + 0.74, 12, 25, '#7f95ad')
      }
      m.enfeitar({ y: P.y, x: nada }, 0)
    })
  } else {
    add([x0 - 0.3, x1 + 0.3, y0 - 0.3, y0, 0], () => {
      caixa(x0 - 0.3, y0 - 0.3, 0, P0 - x0 + 0.3, 0.3, 5, { c: m.parede, l: papel, top: liso('#8c6a49'), o: '#6b5440' })
      caixa(P1, y0 - 0.3, 0, x1 + 0.3 - P1, 0.3, 5, { c: m.parede, l: papel, top: liso('#8c6a49'), o: '#6b5440' })
      vidro(y0 - 0.15, x0 - 0.3, P0 - 0.12, 5, 16)
      vidro(y0 - 0.15, P1 + 0.12, x1 + 0.3, 5, 16)
      caixa(x0 - 0.3, y0 - 0.3, 16, 6.6, 0.3, 1.4, { c: '#8c6a49', top: liso('#6f5236'), o: '#5a4a38' })
      batente(y0 - 0.3, 16)
    })
  }
  const divisoria = (x: number) => caixa(x, y0, 0, 0.3, d, 24, { c: m.parede, top: liso('#8c6a49'), o: '#6b5440' })
  add([x0 - 0.3, x0, y0, y1, 0], () => {
    divisoria(x0 - 0.3)
    m.enfeitar({ y: nada, x: P.x }, 0)
  })
  if (!vizinhaADireita) add([x1, x1 + 0.3, y0, y1, 0], () => divisoria(x1))
  // frente
  if (!embaixo) {
    add([x0 - 0.3, x1 + 0.3, y1, y1 + 0.3, 0], () => {
      caixa(x0, y1, 0, P0 - x0, 0.3, 2, { c: tom(m.parede, -0.1), top: liso('#8c6a49') })
      caixa(P1, y1, 0, x1 - P1, 0.3, 2, { c: tom(m.parede, -0.1), top: liso('#8c6a49') })
      vidro(y1 + 0.15, x0, P0 - 0.12, 2, 15)
      vidro(y1 + 0.15, P1 + 0.12, x1, 2, 15)
      caixa(x0, y1, 15, 6, 0.3, 1.2, { c: '#8c6a49', top: liso('#6f5236'), o: '#5a4a38' })
      batente(y1, 15)
    })
  } else {
    add([x0 - 0.3, x1 + 0.3, y1, y1 + 0.3, 0], () => {
      caixa(x0 - 0.3, y1, 0, 6.6, 0.3, 4, { c: '#d9d2c4', top: liso('#8c6a49'), o: '#5a4a38' })
      vidro(y1 + 0.15, x0 - 0.3, x1 + 0.3, 4, 14)
      caixa(x0 - 0.3, y1, 14, 6.6, 0.3, 1, { c: '#5a4128' })
    })
  }
  m.montar((x, y, w, dd, z, hh, o) => add([x0 + x, x0 + x + w, y0 + y, y0 + y + dd, z], () => caixa(x0 + x, y0 + y, z, w, dd, hh, o)))
}

/* ------------------------------------------------- peças do andar --- */

/** O que se mexe no andar fica anotado enquanto ele é montado. */
type Registro = { pingue: Pt | null; vapores: Pt[]; luzes: Pt[]; elevadores: Pt[] }
let REG: Registro = { pingue: null, vapores: [], luzes: [], elevadores: [] }

/** As peças de montar um andar; cada uma que tem lugar de sentar devolve os lugares. */
function pecas(add: Add) {
  const vaso = (x: number, y: number, s = 1, k = 0) => {
    const c = (['#4caf3e', '#3f9c49', '#62b93a'] as const)[k % 3] as string
    add([x - 0.45 * s, x + 0.45 * s, y - 0.45 * s, y + 0.45 * s, 0], () => {
      caixa(x - 0.3 * s, y - 0.3 * s, 0, 0.6 * s, 0.6 * s, 5, { c: '#b46a3c' })
      caixa(x - 0.45 * s, y - 0.45 * s, 6, 0.9 * s, 0.9 * s, 6 * s, { c, top: ruido(tom(c, 0.25), [tom(c, 0.4), tom(c, 0.1)], 0.35, Math.round(x * 7 + y)) })
      caixa(x - 0.28 * s, y - 0.28 * s, 6 + 6 * s, 0.56 * s, 0.56 * s, 4 * s, { c: tom(c, 0.08), top: ruido(tom(c, 0.35), [tom(c, 0.5), tom(c, 0.2)], 0.35, Math.round(x * 5 + y * 3)) })
    })
  }
  const vasos = (lista: readonly (readonly [number, number, number, number])[]) => lista.forEach(([x, y, s, k]) => vaso(x, y, s, k))
  const luminaria = (x: number, y: number) => {
    REG.luzes.push([x, y])
    add([x - 0.1, x + 0.1, y - 0.1, y + 0.1, 0], () => {
      caixa(x - 0.2, y - 0.2, 0, 0.4, 0.4, 1, { c: '#3a3f55' })
      caixa(x - 0.06, y - 0.06, 1, 0.12, 0.12, 15, { c: '#3a3f55' })
      caixa(x - 0.3, y - 0.3, 16, 0.6, 0.6, 4, { c: '#fff3c4', top: liso('#ffffff') })
    })
  }
  const cadeira = (x: number, y: number, c: string, costas: 'n' | 's'): Pt => {
    add([x, x + 0.7, y, y + 0.7, 0], () => {
      caixa(x, y, 0, 0.7, 0.7, 5, { c })
      caixa(x, costas === 'n' ? y : y + 0.54, 5, 0.7, 0.16, 6, { c: tom(c, -0.1) })
    })
    return [x + 0.35, y + 0.4]
  }
  const sofa = (x: number, y: number, c: string, w = 2.6): Pt[] => {
    add([x, x + w, y, y + 1, 0], () => {
      caixa(x, y, 0, w, 1, 4, { c })
      caixa(x, y, 4, w, 0.3, 6, { c: tom(c, -0.12) })
      caixa(x - 0.2, y, 0, 0.2, 1, 6, { c: tom(c, -0.12) })
      caixa(x + w, y, 0, 0.2, 1, 6, { c: tom(c, -0.12) })
    })
    return [[x + w * 0.27, y + 0.55], [x + w * 0.73, y + 0.55]]
  }
  const mesinha = (x: number, y: number, w = 2, d = 1.2) => add([x, x + w, y, y + d, 0], () => caixa(x, y, 0, w, d, 4, { c: '#7a4f2c', top: TABUA_ESCURA }))
  /** A frente do prédio: mureta, vidro e, se tiver, a porta de entrada. */
  const fachada = (x0: number, x1: number, y: number, porta?: [number, number]) =>
    add([x0, x1, y, y + 0.3, 0], () => {
      const trechos: [number, number][] = porta ? [[x0, porta[0]], [porta[1], x1]] : [[x0, x1]]
      for (const [a, b] of trechos) {
        caixa(a, y, 0, b - a, 0.3, 4, { c: '#d9d2c4', top: liso('#8c6a49'), o: '#5a4a38' })
        vidro(y + 0.15, a, b - (porta && b === porta[0] ? 0.12 : 0), 4, porta ? 16 : 14)
      }
      if (porta) vidro(y + 0.15, porta[0], porta[1], 0, 15, 40)
      caixa(x0, y, porta ? 16 : 14, x1 - x0, 0.3, porta ? 1.4 : 1, { c: porta ? '#2f4fd8' : '#5a4128', top: liso(porta ? '#24409a' : '#4a3420') })
    })
  const paredeEsquerda = (x: number, y1: number) => add([x, x + 0.3, 0, y1, 0], () => caixa(x, 0, 0, 0.3, y1, 24, { c: '#e8e2d6', top: liso('#5a4128'), o: '#5a4a38' }))
  const paredeDireita = (x: number, y0: number, y1: number) => add([x, x + 0.3, y0, y1, 0], () => caixa(x, y0, 0, 0.3, y1 - y0, 4, { c: '#d9d2c4', top: liso('#8c6a49') }))
  /** Pedaço da parede de janelas do fundo onde não há sala. */
  const paredeFundo = (x0: number, x1: number) =>
    add([x0, x1, -0.3, 0, 0], () => {
      caixa(x0, -0.3, 0, x1 - x0, 0.3, 30, { c: '#e8e2d6', l: ruido('#ece6da', ['#e2dccf'], 0.2, Math.round(x0)), top: liso('#5a4128'), o: '#5a4a38' })
      for (let u = x0 + 0.4; u + 1.3 <= x1 - 0.2; u += 2.6) {
        paredeY(-0.3, u, u + 1.3, 11, 26, '#7f95ad')
        paredeY(-0.3, u + 0.12, u + 1.18, 12, 25, ruido('#bfe3f5', ['#d9f0fb'], 0.2, Math.round(u * 3)))
      }
    })
  const banheiros = (x0: number, y1 = 6.3) =>
    add([x0, x0 + 3.4, 0, y1, 0], () => {
      caixa(x0, 0, 0, 3.4, y1, 24, { c: '#dfe6ef', l: ruido('#e6ecf3', ['#dbe2eb'], 0.2, 6), top: ruido('#c9d0da', ['#bfc6d1', '#d3d9e2'], 0.25, 6), o: '#5a4a38' })
      for (const [u, c] of [[x0 + 0.3, '#3f6fe0'], [x0 + 1.9, '#e0679b']] as const) {
        paredeY(y1, u, u + 1.2, 0, 15, '#8c6a49')
        paredeY(y1, u + 0.1, u + 1.1, 0, 14, ruido('#c9a477', ['#bf9a6c'], 0.15, Math.round(u)))
        paredeY(y1, u + 0.35, u + 0.85, 17, 21, c)
      }
    })
  const elevador = (x0: number, y1 = 6.3) => {
    REG.elevadores.push([x0 + 1, y1])
    add([x0, x0 + 2.6, 0, y1, 0], () => {
      caixa(x0, 0, 0, 2.6, y1, 30, { c: '#b9c0cc', l: ruido('#c4cad5', ['#bcc3ce'], 0.15, 7), top: ruido('#9aa2b0', ['#8f97a6', '#a5adbb'], 0.25, 7), o: '#4a5060' })
      paredeY(y1, x0 + 0.4, x0 + 2.2, 0, 17, '#6f7787')
      paredeY(y1, x0 + 0.5, x0 + 1.28, 0, 16, ruido('#dfe5ee', ['#eef2f7', '#cfd6e0'], 0.2, 2))
      paredeY(y1, x0 + 1.32, x0 + 2.1, 0, 16, ruido('#dfe5ee', ['#eef2f7', '#cfd6e0'], 0.2, 3))
      paredeY(y1, x0 + 0.8, x0 + 1.8, 19, 22, '#1a1f33')
      paredeY(y1, x0 + 2.28, x0 + 2.45, 8, 10, '#ffb020')
    })
  }
  const estante = (x: number, y: number) => add([x, x + 0.6, y, y + 2.4, 0], () => caixa(x, y, 0, 0.6, 2.4, 16, { c: '#8c6a49', top: liso('#6f5236') }))
  const bebedouro = (x: number, y: number) =>
    add([x, x + 0.6, y, y + 0.6, 0], () => {
      caixa(x, y, 0, 0.6, 0.6, 9, { c: '#dfe6ef' })
      caixa(x + 0.05, y + 0.05, 9, 0.5, 0.5, 6, { c: '#8fd3ff', top: liso('#bfe8fb') })
    })
  /** Tapete azul com dois sofás, mesa de centro e duas poltronas (7,8 x 5,8). */
  const lounge = (x: number, y: number): Pt[] => {
    add([x, x + 7.8, y, y + 5.8, -1], () => {
      chao(x, x + 7.8, y, y + 5.8, 0, ruido('#2f4fd8', ['#3b5be0', '#2847c4'], 0.25, 5))
      chao(x + 0.3, x + 7.5, y + 0.3, y + 5.5, 0, ruido('#3555de', ['#2f4fd8'], 0.2, 6))
      for (let u = x + 0.4; u < x + 7.4; u += 0.5) {
        const p = pr(u, y + 0.15)
        const q = pr(u, y + 5.65)
        set(Math.round(p[0]), Math.round(p[1]), C('#ffb020'))
        set(Math.round(q[0]), Math.round(q[1]), C('#ffb020'))
      }
    })
    add([x + 1.4, x + 6, y + 0.4, y + 1.4, 0], () => {
      caixa(x + 1.4, y + 0.4, 0, 4.6, 1, 4, { c: '#e9e4f2' })
      caixa(x + 1.4, y + 0.4, 4, 4.6, 0.3, 7, { c: '#d6cfe6' })
    })
    add([x + 1.4, x + 6, y + 4.5, y + 5.5, 0], () => {
      caixa(x + 1.4, y + 4.5, 0, 4.6, 1, 4, { c: '#e9e4f2' })
      caixa(x + 1.4, y + 5.2, 4, 4.6, 0.3, 7, { c: '#d6cfe6' })
    })
    add([x + 2.4, x + 5, y + 2.2, y + 3.5, 0], () => {
      caixa(x + 2.4, y + 2.2, 0, 2.6, 1.3, 4, { c: '#7a4f2c', top: TABUA_ESCURA })
      caixa(x + 2.8, y + 2.5, 4, 0.5, 0.5, 1, { c: '#ffffff' })
      caixa(x + 4, y + 2.4, 4, 0.6, 0.4, 2, { c: '#e0679b' })
    })
    add([x + 0.3, x + 1.3, y + 2.3, y + 3.3, 0], () => {
      caixa(x + 0.3, y + 2.3, 0, 1, 1, 4, { c: '#ffb020' })
      caixa(x + 0.3, y + 2.3, 4, 0.25, 1, 6, { c: '#e09a10' })
    })
    add([x + 6.6, x + 7.6, y + 2.3, y + 3.3, 0], () => {
      caixa(x + 6.6, y + 2.3, 0, 1, 1, 4, { c: '#ffb020' })
      caixa(x + 7.35, y + 2.3, 4, 0.25, 1, 6, { c: '#e09a10' })
    })
    return [
      [x + 2.4, y + 0.95], [x + 3.7, y + 0.95], [x + 5, y + 0.95], [x + 2.4, y + 4.95], [x + 3.7, y + 4.95], [x + 5, y + 4.95],
      [x + 0.8, y + 2.8], [x + 7.1, y + 2.8],
    ]
  }
  const pingue = (x: number, y: number) => {
    REG.pingue = [x, y]
    add([x, x + 4.2, y, y + 2.3, 0], () => {
      for (const [px, py] of [[x + 0.1, y + 0.1], [x + 4, y + 0.1], [x + 0.1, y + 2], [x + 4, y + 2]] as const) caixa(px, py, 0, 0.15, 0.15, 6, { c: '#2a2f44' })
      caixa(x, y, 6, 4.2, 2.3, 1, { c: '#2f8a52', top: ruido('#2f9a5a', ['#35a462'], 0.15, 2) })
      linha(pr(x, y + 1.15, 7), pr(x + 4.2, y + 1.15, 7), '#ffffff')
      caixa(x + 2.05, y, 7, 0.1, 2.3, 2, { c: '#eef1f6' })
    })
  }
  const mesaComunitaria = (x: number, y: number): Pt[] => {
    add([x, x + 4.1, y, y + 1.3, 0], () => {
      caixa(x, y, 0, 4.1, 1.3, 7, { c: '#cbb88f', top: ruido('#f1e3c8', ['#fff4dc'], 0.2, 3) })
      caixa(x + 0.5, y + 0.3, 7, 0.5, 0.4, 1, { c: '#ffffff' })
      caixa(x + 2.7, y + 0.4, 7, 0.3, 0.3, 2, { c: '#bfe8fb' })
    })
    return [
      cadeira(x + 0.4, y - 0.8, '#3f6fe0', 'n'),
      cadeira(x + 2.7, y - 0.8, '#ffb020', 'n'),
      cadeira(x + 0.4, y + 1.4, '#2fb36d', 's'),
      cadeira(x + 2.7, y + 1.4, '#e0679b', 's'),
    ]
  }
  const cafe = (x: number, y: number): Pt[] => {
    REG.vapores.push([x + 0.9, y + 0.4])
    add([x, x + 4.8, y, y + 1.4, 0], () => {
      caixa(x, y, 0, 4.8, 1.4, 8, { c: '#3a3f55', l: tabuas('#4a5070', '#434966', '#30354a'), top: liso('#eef1f6') })
      caixa(x + 0.4, y + 0.2, 8, 1, 0.8, 5, { c: '#2a2f44', top: liso('#3a4160') })
      caixa(x + 2.2, y + 0.3, 8, 0.5, 0.5, 2, { c: '#e0679b' })
      caixa(x + 3.2, y + 0.3, 8, 0.4, 0.4, 3, { c: '#bfe8fb' })
      caixa(x + 4, y + 0.25, 8, 0.5, 0.5, 1, { c: '#ffb020' })
    })
    const lugares: Pt[] = []
    for (const dx of [0.5, 2, 3.5]) {
      add([x + dx, x + dx + 0.6, y + 1.8, y + 2.4, 0], () => {
        caixa(x + dx + 0.25, y + 2.05, 0, 0.1, 0.1, 5, { c: '#3a3f55' })
        caixa(x + dx, y + 1.8, 5, 0.6, 0.6, 1, { c: '#ffb020' })
      })
      lugares.push([x + dx + 0.3, y + 2.1])
    }
    return lugares
  }
  const marca = (cx: number, cy: number) =>
    add([cx - 1.6, cx + 1.6, cy - 1.6, cy + 1.6, -1], () => {
      for (let a = 0; a < 120; a++) {
        const t = (a / 120) * Math.PI * 2
        const p = pr(cx + Math.cos(t) * 1.5, cy + Math.sin(t) * 1.5)
        set(Math.round(p[0]), Math.round(p[1]), C('#2f4fd8'))
        set(Math.round(p[0]) + 1, Math.round(p[1]), C('#2f4fd8'))
        set(Math.round(p[0]), Math.round(p[1]) + 1, C('#24409a'))
      }
      const p = pr(cx + 1.2, cy - 1.2)
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) set(Math.round(p[0]) + dx, Math.round(p[1]) + dy, C('#ffb020'))
    })
  const recepcao = (x: number, y: number) => {
    add([x, x + 4.8, y, y + 1.4, 0], () => {
      caixa(x, y, 0, 4.8, 1.4, 8, { c: '#2f4fd8', l: ruido('#3555de', ['#2f4fd8'], 0.15, 4), top: liso('#e6ebf2') })
      caixa(x + 1.6, y + 0.6, 8, 0.15, 1, 4, { c: '#1a1f33' })
      caixa(x + 3.2, y + 0.7, 8, 0.4, 0.4, 2, { c: '#ffb020' })
      for (let a = 0; a < 40; a++) {
        const t = (a / 40) * Math.PI * 2
        const p = pr(x + 2.4 + Math.cos(t) * 0.45, y + 1.4, 4 + Math.sin(t) * 2.6)
        set(Math.round(p[0]), Math.round(p[1]), C('#ffffff'))
      }
    })
    cadeira(x + 1.2, y - 0.8, '#3a3f55', 'n')
  }
  /** Bancada aberta com n lugares: mesa, monitor e cadeira virada para a mesa. */
  const bancada = (x: number, y: number, n: number): Pt[] => {
    const lugares: Pt[] = []
    for (let i = 0; i < n; i++) {
      const dx = x + i * 2.2
      add([dx, dx + 2, y, y + 1, 0], () => {
        caixa(dx, y, 0, 2, 1, 8, { c: '#c9cfdb', top: ruido('#e7ebf2', ['#f2f5f9'], 0.2, Math.round(dx * 9)) })
        caixa(dx + 0.55, y + 0.12, 8, 0.9, 0.14, 7, { c: '#1a1f33' })
        caixa(dx + 0.6, y + 0.18, 9, 0.8, 0.06, 5, { c: ['#2f4fd8', '#1f9e8a', '#8a5be0', '#d9822b'][i % 4] as string })
        caixa(dx + 0.6, y + 0.55, 8, 0.7, 0.25, 0.6, { c: '#2a2f44' })
      })
      lugares.push(cadeira(dx + 0.65, y + 1.15, ['#1f2440', '#2a3057', '#3a3f55'][i % 3] as string, 's'))
    }
    return lugares
  }
  const sofaSul = (x: number, y: number, c: string, w = 2.6): Pt[] => {
    add([x, x + w, y, y + 1, 0], () => {
      caixa(x, y, 0, w, 1, 4, { c })
      caixa(x, y + 0.7, 4, w, 0.3, 6, { c: tom(c, -0.12) })
      caixa(x - 0.2, y, 0, 0.2, 1, 6, { c: tom(c, -0.12) })
      caixa(x + w, y, 0, 0.2, 1, 6, { c: tom(c, -0.12) })
    })
    return [[x + w * 0.27, y + 0.45], [x + w * 0.73, y + 0.45]]
  }
  const poltrona = (x: number, y: number, c: string, lado: 'n' | 's'): Pt[] => {
    add([x, x + 1, y, y + 1, 0], () => {
      caixa(x, y, 0, 1, 1, 4, { c })
      caixa(x, lado === 'n' ? y : y + 0.75, 4, 1, 0.25, 6, { c: tom(c, -0.12) })
      caixa(x, y, 4, 0.15, 1, 2, { c: tom(c, -0.12) })
      caixa(x + 0.85, y, 4, 0.15, 1, 2, { c: tom(c, -0.12) })
    })
    return [[x + 0.5, y + 0.55]]
  }
  const tapete = (x: number, y: number, c: string, w = 3, d = 2) =>
    add([x, x + w, y, y + d, -1], () => {
      chao(x, x + w, y, y + d, 0, ruido(c, [tom(c, 0.08), tom(c, -0.08)], 0.25, Math.round(x * 3 + y)))
      chao(x + 0.2, x + w - 0.2, y + 0.2, y + d - 0.2, 0, ruido(tom(c, 0.06), [c], 0.2, Math.round(x + y * 5)))
      for (let u = x + 0.3; u < x + w - 0.2; u += 0.5) {
        const p = pr(u, y + 0.1)
        const q = pr(u, y + d - 0.1)
        set(Math.round(p[0]), Math.round(p[1]), C('#ffb020'))
        set(Math.round(q[0]), Math.round(q[1]), C('#ffb020'))
      }
    })
  const caixaSimples = (x: number, y: number, w: number, d: number, h: number, c: string) =>
    add([x, x + w, y, y + d, 0], () => caixa(x, y, 0, w, d, h, { c, top: ruido(tom(c, 0.12), [tom(c, 0.2), c], 0.25, Math.round(x * 7 + y)) }))
  return { sofaSul, poltrona, tapete, vaso, vasos, luminaria, cadeira, sofa, mesinha, fachada, paredeEsquerda, paredeDireita, paredeFundo, banheiros, elevador, estante, bebedouro, lounge, pingue, mesaComunitaria, cafe, marca, recepcao, bancada, caixaSimples }
}

/* ------------------------------------------- móveis de quem administra --- */

export type Categoria = 'trabalho' | 'descanso' | 'jogos' | 'cozinha' | 'plantas' | 'decoracao' | 'seres'
export const CATEGORIAS: { id: Categoria; nome: string; icone: string }[] = [
  { id: 'trabalho', nome: 'Trabalho', icone: 'fa-laptop' },
  { id: 'descanso', nome: 'Descanso', icone: 'fa-couch' },
  { id: 'jogos', nome: 'Jogos', icone: 'fa-gamepad' },
  { id: 'cozinha', nome: 'Cozinha e bar', icone: 'fa-mug-hot' },
  { id: 'plantas', nome: 'Plantas', icone: 'fa-seedling' },
  { id: 'decoracao', nome: 'Decoração', icone: 'fa-star' },
  { id: 'seres', nome: 'Bichinhos', icone: 'fa-paw' },
]
export type ItemDoCatalogo = {
  nome: string
  w: number
  d: number
  cat: Categoria
  cores?: string[]
  gira?: boolean
  /** Lugares de sentar, relativos ao canto (virado para 'n'); no 's' giram meia volta. */
  assentos?: Pt[]
  /** Altura aproximada (px do tabuleiro), para o desenho simples do pixel antigo. */
  h?: number
  /** Cor do desenho simples do pixel antigo. */
  c?: string
}
const CORES_ESTOFADO = ['#3f6fe0', '#e0679b', '#2fb36d', '#ffb020', '#e9e4f2', '#8a5be0', '#c2484f']
const CORES_TAPETE = ['#2f4fd8', '#c2484f', '#2fb36d', '#8a5be0', '#d6a21e', '#3a3f55']
/**
 * Catálogo de móveis do modo criativo (SPEC-20261010-0451, ampliado na SPEC-20261010-2330):
 * tamanho no chão (w x d, unidades do tabuleiro), categoria, cores, se vira e onde se senta.
 * O desenho de cada um nos visuais de blocos e realista vem do Blender (public/escritorio/moveis).
 */
export const CATALOGO = {
  // trabalho
  mesa: { nome: 'Mesa de trabalho', w: 2, d: 1.9, cat: 'trabalho' },
  mesa_pe: { nome: 'Mesa em pé', w: 1.8, d: 1.2, cat: 'trabalho', h: 14, c: '#e9edf3', assentos: [[0.9, 1.0]] },
  estacao_dupla: { nome: 'Estação dupla', w: 2.4, d: 2.8, cat: 'trabalho', h: 12, c: '#e9edf3', assentos: [[1.2, 0.4], [1.2, 2.4]] },
  cabine_fone: { nome: 'Cabine de ligação', w: 1.7, d: 1.7, cat: 'trabalho', h: 26, c: '#3f6fe0', assentos: [[0.85, 0.95]] },
  comunitaria: { nome: 'Mesa comunitária', w: 4.1, d: 3.1, cat: 'trabalho' },
  impressora: { nome: 'Impressora', w: 1.0, d: 0.9, cat: 'trabalho', h: 12, c: '#f3f1ec' },
  armario: { nome: 'Armário', w: 1.8, d: 0.8, cat: 'trabalho', h: 18, c: '#c9cfdb' },
  lousa_rodas: { nome: 'Quadro com rodinhas', w: 2.0, d: 0.7, cat: 'trabalho', h: 22, c: '#fbfbf8' },
  telao: { nome: 'Telão', w: 2.4, d: 0.9, cat: 'trabalho', h: 24, c: '#1a1f33' },
  // descanso
  sofa: { nome: 'Sofá', w: 3, d: 1, cat: 'descanso', cores: CORES_ESTOFADO, gira: true },
  sofa_l: { nome: 'Sofá em L', w: 3.4, d: 2.6, cat: 'descanso', cores: CORES_ESTOFADO, gira: true, h: 9, assentos: [[0.7, 0.55], [1.7, 0.55], [2.8, 0.7], [2.85, 1.8]] },
  poltrona: { nome: 'Poltrona', w: 1, d: 1, cat: 'descanso', cores: CORES_ESTOFADO, gira: true },
  balanco: { nome: 'Poltrona de balanço', w: 1.3, d: 1.3, cat: 'descanso', gira: true, h: 10, c: '#c9935a', assentos: [[0.65, 0.7]] },
  puff_gigante: { nome: 'Puff gigante', w: 1.7, d: 1.7, cat: 'descanso', cores: CORES_ESTOFADO, h: 6, assentos: [[0.6, 0.8], [1.1, 0.9]] },
  rede: { nome: 'Rede', w: 3.4, d: 1.3, cat: 'descanso', h: 14, c: '#ffb020', assentos: [[1.7, 0.65]] },
  mesinha: { nome: 'Mesa de centro', w: 2, d: 1.2, cat: 'descanso' },
  lareira: { nome: 'Lareira', w: 2.2, d: 0.9, cat: 'descanso', h: 14, c: '#8a5a3a' },
  luminaria: { nome: 'Luminária', w: 0.6, d: 0.6, cat: 'descanso' },
  // jogos
  pingue: { nome: 'Pingue-pongue', w: 4.2, d: 2.3, cat: 'jogos' },
  fliperama: { nome: 'Fliperama', w: 1.1, d: 1.0, cat: 'jogos', h: 22, c: '#8a5be0', assentos: [[0.55, 1.2]] },
  pebolim: { nome: 'Pebolim', w: 2.4, d: 1.5, cat: 'jogos', h: 10, c: '#2a9a58', assentos: [[1.2, -0.2], [1.2, 1.7]] },
  sinuca: { nome: 'Sinuca', w: 4.2, d: 2.5, cat: 'jogos', h: 10, c: '#2a7a52' },
  dardos: { nome: 'Alvo de dardos', w: 1.0, d: 0.6, cat: 'jogos', h: 22, c: '#c2484f' },
  videogame: { nome: 'Videogame', w: 2.8, d: 2.2, cat: 'jogos', h: 14, c: '#1a1f33', assentos: [[0.9, 1.8], [1.9, 1.8]] },
  piano: { nome: 'Piano', w: 2.2, d: 1.3, cat: 'jogos', h: 14, c: '#1a1a1a', assentos: [[1.1, 1.3]] },
  guitarra: { nome: 'Guitarra no suporte', w: 0.8, d: 0.7, cat: 'jogos', h: 16, c: '#e5484d' },
  // cozinha e bar
  cafe: { nome: 'Balcão de café', w: 4.8, d: 2.4, cat: 'cozinha' },
  bebedouro: { nome: 'Bebedouro', w: 0.6, d: 0.6, cat: 'cozinha' },
  lanches: { nome: 'Máquina de lanches', w: 1.3, d: 1.0, cat: 'cozinha', h: 22, c: '#c2484f' },
  geladeira: { nome: 'Geladeira', w: 1.1, d: 1.0, cat: 'cozinha', h: 22, c: '#e9edf3' },
  chopeira: { nome: 'Chopeira', w: 1.8, d: 1.1, cat: 'cozinha', h: 14, c: '#8a5a3a' },
  mesa_alta: { nome: 'Mesa alta', w: 1.8, d: 1.8, cat: 'cozinha', h: 12, c: '#c9935a', assentos: [[0.25, 0.9], [1.55, 0.9], [0.9, 0.25], [0.9, 1.6]] },
  // plantas
  planta: { nome: 'Planta', w: 0.9, d: 0.9, cat: 'plantas' },
  arvore: { nome: 'Árvore no vaso', w: 1.4, d: 1.4, cat: 'plantas', h: 30, c: '#3f9c49' },
  jardim: { nome: 'Jardim vertical', w: 2.6, d: 0.5, cat: 'plantas', h: 24, c: '#3f9c49' },
  cacto: { nome: 'Cacto', w: 0.8, d: 0.8, cat: 'plantas', h: 14, c: '#4caf3e' },
  samambaia: { nome: 'Samambaia', w: 1.0, d: 1.0, cat: 'plantas', h: 12, c: '#3f9c49' },
  canteiro: { nome: 'Canteiro de flores', w: 2.4, d: 0.9, cat: 'plantas', h: 8, c: '#e0679b' },
  terrario: { nome: 'Terrário', w: 0.9, d: 0.9, cat: 'plantas', h: 12, c: '#bfe8fb' },
  // decoração
  tapete: { nome: 'Tapete', w: 3, d: 2, cat: 'decoracao', cores: CORES_TAPETE },
  tapete_redondo: { nome: 'Tapete redondo', w: 2.6, d: 2.6, cat: 'decoracao', cores: CORES_TAPETE, h: 0.3 },
  estante: { nome: 'Estante', w: 0.6, d: 2.4, cat: 'decoracao' },
  neon: { nome: 'Letreiro neon', w: 2.4, d: 0.5, cat: 'decoracao', h: 20, c: '#e0679b' },
  relogio: { nome: 'Relógio de pé', w: 0.8, d: 0.7, cat: 'decoracao', h: 24, c: '#7a5236' },
  trofeus: { nome: 'Vitrine de troféus', w: 1.6, d: 0.7, cat: 'decoracao', h: 20, c: '#7a5236' },
  luminaria_arco: { nome: 'Luminária de arco', w: 1.6, d: 1.0, cat: 'decoracao', h: 24, c: '#3a3f55' },
  biombo: { nome: 'Divisória com plantas', w: 2.6, d: 0.5, cat: 'decoracao', h: 18, c: '#c9935a' },
  cavalete: { nome: 'Cavalete com quadro', w: 1.1, d: 0.9, cat: 'decoracao', h: 20, c: '#c9935a' },
  // bichinhos
  cachorro: { nome: 'Cachorro', w: 1.2, d: 0.8, cat: 'seres', h: 8, c: '#c98a4a' },
  gato: { nome: 'Gato', w: 1.0, d: 1.0, cat: 'seres', h: 5, c: '#ffb020' },
  papagaio: { nome: 'Papagaio', w: 0.9, d: 0.9, cat: 'seres', h: 20, c: '#2fb36d' },
  tartaruga: { nome: 'Tartaruga', w: 1.3, d: 1.0, cat: 'seres', h: 8, c: '#4caf3e' },
  coelho: { nome: 'Coelho', w: 0.8, d: 0.8, cat: 'seres', h: 6, c: '#f3efe8' },
  aquario_redondo: { nome: 'Aquário redondo', w: 0.9, d: 0.9, cat: 'seres', h: 12, c: '#7fd4ff' },
} satisfies Record<string, ItemDoCatalogo>
export type MovelTipo = keyof typeof CATALOGO
export type Movel = { id: string; kind: MovelTipo; x: number; y: number; variant: 'n' | 's'; color: string | null }
export const TIPOS_DE_MOVEL = Object.keys(CATALOGO) as MovelTipo[]
export const itemDoCatalogo = (k: MovelTipo): ItemDoCatalogo => CATALOGO[k] as ItemDoCatalogo

/** Lugares de sentar de um móvel do catálogo, no tabuleiro (para os móveis novos). */
function assentosDoCatalogo(m: Movel): Pt[] {
  const it = itemDoCatalogo(m.kind)
  return (it.assentos ?? []).map(([ax, ay]) => (m.variant === 's' && it.gira ? [m.x + it.w - ax, m.y + it.d - ay] : [m.x + ax, m.y + ay]) as Pt)
}

/** Desenha um móvel (canto de cima à esquerda em x, y) e devolve onde dá para sentar. */
function desenharMovel(k: ReturnType<typeof pecas>, m: Movel): Pt[] {
  const { x, y } = m
  const cor = m.color ?? undefined
  switch (m.kind) {
    case 'planta':
      k.vaso(x + 0.45, y + 0.45, 1, Math.round(x + y) % 3)
      return []
    case 'sofa':
      return m.variant === 's' ? k.sofaSul(x + 0.2, y, cor ?? '#3f6fe0') : k.sofa(x + 0.2, y, cor ?? '#3f6fe0')
    case 'poltrona':
      return k.poltrona(x, y, cor ?? '#ffb020', m.variant)
    case 'mesinha':
      k.mesinha(x, y)
      return []
    case 'luminaria':
      k.luminaria(x + 0.3, y + 0.3)
      return []
    case 'estante':
      k.estante(x, y)
      return []
    case 'bebedouro':
      k.bebedouro(x, y)
      return []
    case 'mesa':
      return k.bancada(x, y, 1)
    case 'pingue':
      k.pingue(x, y)
      return []
    case 'cafe':
      return k.cafe(x, y)
    case 'comunitaria':
      return k.mesaComunitaria(x, y + 0.8)
    case 'tapete':
      k.tapete(x, y, cor ?? '#2f4fd8')
      return []
    default: {
      // móveis novos: no pixel antigo, um bloco simples na cor do item (o desenho bonito vem do Blender)
      const it = itemDoCatalogo(m.kind)
      k.caixaSimples(x + 0.05, y + 0.05, it.w - 0.1, it.d - 0.1, it.h ?? 8, cor ?? it.c ?? '#c9cfdb')
      return assentosDoCatalogo(m)
    }
  }
}

/** Miniatura de um móvel do catálogo, no chão de tábuas, para a gaveta do editor. */
export function miniaturaDoMovel(tipo: MovelTipo): HTMLCanvasElement {
  const item = itemDoCatalogo(tipo)
  const margem = 0.6
  const w = Math.ceil((item.w + margem * 2) * TX)
  const h = Math.ceil((item.d + margem * 2) * TY + 30)
  const antes = T
  T = novoAlvo(w, h, margem * TX, 26 + margem * TY)
  chao(-margem, item.w + margem, -margem, item.d + margem, 0, PISO)
  const itens: Caixa[] = []
  const regAntes = REG
  REG = { pingue: null, vapores: [], luzes: [], elevadores: [] }
  desenharMovel(pecas((b, f) => itens.push({ b, f })), { id: 'mini', kind: tipo, x: 0, y: 0, variant: 'n', color: null })
  for (const it of ordenar(itens)) it.f()
  REG = regAntes
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  ;(c.getContext('2d') as CanvasRenderingContext2D).putImageData(T.img, 0, 0)
  T = antes
  return c
}

/* --------------------------------------------- os tabuleiros prontos --- */

export type TabuleiroId = 'sede' | 'startup' | 'coworking' | 'grande' | 'livre'
export type Tabuleiro = {
  id: TabuleiroId
  nome: string
  descricao: string
  predio: Retangulo
  vagas: Vaga[]
  /** Onde o clique conta como área central; o primeiro é o contorno do destaque. */
  centrais: Retangulo[]
  pisos: { r: Retangulo; a: string; b: string }[]
  /** Tapete escuro da porta de entrada. */
  entrada: Retangulo | null
  placaCentral: [number, number, number]
  /** Monta o andar e devolve os lugares de sentar da área central, do melhor para o pior. */
  montar: (add: Add) => Pt[]
  /**
   * Visual realista do andar (SPEC-20261010-1813): imagem renderizada no Blender na mesma vista
   * e no mesmo enquadramento do tabuleiro, posta por cima do pixel art para quem escolher
   * "Realista". Sem ela, o andar continua em pixel art. `casca` é o andar sem as salas; cada sala
   * vem da imagem do seu modelo (AndarRealista.tsx, SPEC-20261010-1914).
   */
  realista?: { casca: string }
  /** Pixel 2.0 de blocos (SPEC-20261010-2133): mesmo esquema do realista, no lugar do pixel desenhado no código. */
  blocos?: { casca: string }
}

export const TABULEIROS: readonly Tabuleiro[] = [
  {
    id: 'sede',
    nome: 'Sede',
    descricao: '9 salas, corredor com lounge, pingue-pongue e recepção',
    predio: { x0: -5, x1: 35.5, y0: 0, y1: 24.6 },
    vagas: [
      vaga(1, -4.4, 0.3, 'cima'), vaga(2, 1.9, 0.3, 'cima'), vaga(3, 8.2, 0.3, 'cima'), vaga(4, 18.2, 0.3, 'cima'), vaga(7, 24.5, 0.3, 'cima'),
      vaga(5, -4.4, 18.3, 'baixo'), vaga(6, 1.9, 18.3, 'baixo'), vaga(8, 18.2, 18.3, 'baixo'), vaga(9, 24.5, 18.3, 'baixo'),
    ],
    centrais: [
      { x0: -4.7, x1: 35.2, y0: 6.6, y1: 18 },
      { x0: 8.2, x1: 17.9, y0: 18.3, y1: 24.3 },
      { x0: 30.8, x1: 35.2, y0: 18.3, y1: 24.3 },
    ],
    pisos: [{ r: { x0: 8.2, x1: 17.9, y0: 18.3, y1: 24.3 }, a: '#ece7dd', b: '#e2dccf' }],
    entrada: { x0: 12.2, x1: 13.9, y0: 23.4, y1: 24.2 },
    placaCentral: [19.8, 9.6, 20],
    realista: { casca: 'sede.webp' },
    blocos: { casca: 'sede.webp' },
    montar(add) {
      const k = pecas(add)
      k.paredeEsquerda(-5, 24.3)
      k.paredeFundo(33.4, 35.5)
      k.banheiros(14.5)
      k.elevador(30.8)
      const L: Pt[] = [...k.lounge(9.6, 9)]
      L.push(...k.sofa(-3.6, 8, '#3f6fe0'))
      k.mesinha(-3.3, 9.6)
      L.push(...k.sofa(-3.6, 11.4, '#e0679b'))
      k.estante(-4.7, 13.4)
      k.pingue(21.8, 9.6)
      L.push(...k.mesaComunitaria(21.9, 13.4))
      L.push(...k.cafe(28.2, 9.2))
      k.bebedouro(34.2, 1.6)
      k.marca(19.8, 11.8)
      k.luminaria(8.8, 12.6)
      k.luminaria(18, 9.2)
      k.vasos([[7.9, 7.6, 0.9, 0], [17.6, 7.6, 1, 1], [30.2, 7.6, 0.9, 2], [34.6, 7.8, 1, 0], [-4.1, 17.2, 1, 1], [7.6, 17.2, 0.9, 2], [19.6, 17.3, 1, 0], [28.6, 17.3, 0.9, 1], [34.6, 16.8, 1, 2]])
      k.fachada(7.9, 18.2, 24.3, [12.2, 13.9])
      k.recepcao(10.4, 19.6)
      L.push(...k.sofa(9, 22.6, '#2fb36d', 2.2), ...k.sofa(15, 22.6, '#2fb36d', 2.2))
      k.vasos([[9, 19.4, 0.9, 0], [17.2, 19.6, 1, 1], [34.6, 19.2, 0.9, 2]])
      L.push(...k.sofa(31.6, 19.6, '#ffb020'))
      k.mesinha(31.9, 21.1, 2, 1)
      L.push(...k.sofa(31.6, 22.6, '#3f6fe0'))
      k.fachada(30.8, 35.2, 24.3)
      k.paredeDireita(35.2, 6.3, 24.6)
      return L
    },
  },
  {
    id: 'startup',
    nome: 'Startup',
    descricao: '3 salas e um salão aberto com bancadas, lounge e café',
    predio: { x0: 0, x1: 27.5, y0: 0, y1: 15.7 },
    vagas: [vaga(2, 0.6, 0.3, 'cima'), vaga(1, 6.9, 0.3, 'cima'), vaga(4, 13.2, 0.3, 'cima')],
    centrais: [{ x0: 0.3, x1: 27.2, y0: 6.6, y1: 15.4 }],
    pisos: [],
    entrada: { x0: 12.4, x1: 14.1, y0: 14.5, y1: 15.3 },
    placaCentral: [13.3, 7.8, 20],
    realista: { casca: 'startup.webp' },
    blocos: { casca: 'startup.webp' },
    montar(add) {
      const k = pecas(add)
      k.paredeEsquerda(0, 15.4)
      k.banheiros(19.5)
      k.elevador(23.2)
      k.paredeFundo(25.8, 27.5)
      k.bebedouro(26.5, 1.4)
      const L: Pt[] = [...k.lounge(9.4, 8.4)]
      L.push(...k.bancada(0.9, 7.8, 3), ...k.bancada(0.9, 11.4, 3))
      L.push(...k.cafe(19.8, 7.6))
      L.push(...k.mesaComunitaria(20.4, 12.1))
      k.luminaria(8.6, 9)
      k.luminaria(18.2, 13.6)
      k.vasos([[8.3, 7.3, 0.9, 0], [18.6, 7.3, 1, 1], [26.6, 7.5, 0.9, 2], [26.6, 14.4, 1, 0], [0.9, 14.7, 0.9, 1], [18.4, 10.6, 0.8, 2]])
      k.fachada(0, 27.5, 15.4, [12.4, 14.1])
      return L
    },
  },
  {
    id: 'coworking',
    nome: 'Coworking',
    descricao: '4 salas e um espaço aberto grande com bancadas compartilhadas',
    predio: { x0: 0, x1: 36, y0: 0, y1: 22 },
    vagas: [vaga(2, 0.6, 0.3, 'cima'), vaga(7, 6.9, 0.3, 'cima'), vaga(5, 0.6, 15.7, 'baixo'), vaga(3, 6.9, 15.7, 'baixo')],
    centrais: [
      { x0: 0.3, x1: 35.7, y0: 6.6, y1: 15.4 },
      { x0: 13.2, x1: 35.7, y0: 15.7, y1: 21.7 },
    ],
    pisos: [{ r: { x0: 13.2, x1: 35.7, y0: 15.7, y1: 21.7 }, a: '#ece7dd', b: '#e2dccf' }],
    entrada: { x0: 20.6, x1: 22.3, y0: 20.8, y1: 21.6 },
    placaCentral: [10.2, 9.2, 20],
    realista: { casca: 'coworking.webp' },
    blocos: { casca: 'coworking.webp' },
    montar(add) {
      const k = pecas(add)
      k.paredeEsquerda(0, 21.7)
      k.banheiros(13.2)
      k.paredeFundo(16.6, 31.4)
      k.elevador(31.4)
      k.paredeFundo(34, 36)
      const L: Pt[] = []
      L.push(...k.bancada(13.6, 7.4, 4), ...k.bancada(23.8, 7.4, 4), ...k.bancada(13.6, 10.9, 4), ...k.bancada(23.8, 10.9, 4))
      L.push(...k.sofa(1.2, 12.4, '#ffb020'), ...k.sofa(4.6, 12.4, '#3f6fe0'))
      k.pingue(1.6, 8.6)
      k.marca(10, 10.8)
      k.luminaria(23.1, 9.3)
      k.luminaria(23.1, 12.8)
      k.recepcao(15, 17.2)
      L.push(...k.sofa(14, 20.2, '#2fb36d', 2.2))
      L.push(...k.cafe(25.4, 16.4))
      L.push(...k.mesaComunitaria(31, 18.2))
      k.bebedouro(34.9, 1.6)
      k.vasos([[7.5, 14.6, 0.9, 0], [12.6, 7.4, 1, 1], [33, 9, 0.9, 2], [33.4, 13.4, 1, 0], [13.6, 16.3, 0.9, 2], [35.1, 16.4, 0.9, 1], [19.6, 20.6, 0.8, 0]])
      k.fachada(12.9, 36, 21.7, [20.6, 22.3])
      return L
    },
  },
  {
    id: 'grande',
    nome: 'Andar grande',
    descricao: '13 salas em duas alas, salão largo com dois lounges',
    predio: { x0: -5, x1: 47, y0: 0, y1: 28.6 },
    vagas: [
      vaga(1, -4.4, 0.3, 'cima'), vaga(2, 1.9, 0.3, 'cima'), vaga(3, 8.2, 0.3, 'cima'), vaga(4, 18.2, 0.3, 'cima'), vaga(7, 24.5, 0.3, 'cima'),
      vaga(10, 33.7, 0.3, 'cima'), vaga(11, 40, 0.3, 'cima'),
      vaga(5, -4.4, 22.3, 'baixo'), vaga(6, 1.9, 22.3, 'baixo'), vaga(8, 18.2, 22.3, 'baixo'), vaga(9, 24.5, 22.3, 'baixo'), vaga(12, 33.7, 22.3, 'baixo'),
    ],
    centrais: [
      { x0: -4.7, x1: 46.7, y0: 6.6, y1: 22 },
      { x0: 8.2, x1: 17.9, y0: 22.3, y1: 28.3 },
      { x0: 30.8, x1: 33.4, y0: 22.3, y1: 28.3 },
      { x0: 40, x1: 46.7, y0: 22.3, y1: 28.3 },
    ],
    pisos: [{ r: { x0: 8.2, x1: 17.9, y0: 22.3, y1: 28.3 }, a: '#ece7dd', b: '#e2dccf' }],
    entrada: { x0: 12.2, x1: 13.9, y0: 27.4, y1: 28.2 },
    placaCentral: [21, 16.6, 20],
    realista: { casca: 'grande.webp' },
    blocos: { casca: 'grande.webp' },
    montar(add) {
      const k = pecas(add)
      k.paredeEsquerda(-5, 28.3)
      k.banheiros(14.5)
      k.elevador(30.8)
      k.paredeFundo(46.3, 47)
      const L: Pt[] = [...k.lounge(9.6, 9), ...k.lounge(32.4, 9)]
      L.push(...k.sofa(-3.6, 8, '#3f6fe0'))
      k.mesinha(-3.3, 9.6)
      L.push(...k.sofa(-3.6, 11.4, '#e0679b'))
      k.estante(-4.7, 13.4)
      k.pingue(21.8, 9.6)
      L.push(...k.mesaComunitaria(21.9, 13.4))
      L.push(...k.cafe(41.4, 9.2))
      L.push(...k.bancada(-3.8, 16.6, 6), ...k.bancada(32.8, 16.6, 6))
      k.marca(21, 18.6)
      k.luminaria(8.8, 12.6)
      k.luminaria(18, 9.2)
      k.luminaria(31.6, 12.6)
      k.vasos([[7.9, 7.6, 0.9, 0], [17.6, 7.6, 1, 1], [30.2, 7.6, 0.9, 2], [46.2, 14.6, 1, 0], [-4.1, 21.2, 1, 1], [14.6, 20.6, 0.9, 2], [27.4, 20.6, 1, 0], [46.2, 21.2, 0.9, 1]])
      k.fachada(7.9, 18.2, 28.3, [12.2, 13.9])
      k.recepcao(10.4, 23.6)
      L.push(...k.sofa(9, 26.6, '#2fb36d', 2.2), ...k.sofa(15, 26.6, '#2fb36d', 2.2))
      k.vaso(31.6, 24, 0.9, 2)
      k.bebedouro(32.3, 26.6)
      k.fachada(30.8, 33.4, 28.3)
      L.push(...k.sofa(41, 23.6, '#ffb020'))
      k.mesinha(41.3, 25.1, 2, 1)
      L.push(...k.sofa(41, 26.6, '#3f6fe0'))
      k.vaso(45.8, 23.4, 0.9, 0)
      k.fachada(40, 46.7, 28.3)
      k.paredeDireita(46.7, 6.3, 28.6)
      return L
    },
  },
]
export const tabuleiro = (id: string | null | undefined): Tabuleiro => TABULEIROS.find((t) => t.id === id) ?? (TABULEIROS[0] as Tabuleiro)
/** Tamanho do tabuleiro em pixels e onde fica a origem. */
export function medidas(t: Tabuleiro) {
  const { x0, x1, y0, y1 } = t.predio
  return { largura: Math.round((x1 - x0) * TX), altura: Math.round(34 + (y1 - y0) * TY + 11), ox: -x0 * TX, oy: 34 - y0 * TY }
}

/* ----------------------------------------------------------------- a cena --- */

type Animado = { sala: SalaNoMapa; vaga: Vaga }

/* ------------------------------------------------- placas em pixel art --- */

/**
 * O nome da sala é desenhado no próprio tabuleiro numa fonte pixel
 * (Silkscreen, 8 px de desenho), pixel por pixel: a fonte é escrita 8 vezes
 * maior e cada quadradinho é lido no centro, então sai exatamente o desenho da
 * fonte, aceso ou apagado. Assim a placa amplia junto com o resto, nítida.
 */
const FAMILIA_PIXEL = 'Silkscreen'
const TAMANHO_PIXEL = 8
const AMPLIA = 8
const FONTE_PIXEL = `${TAMANHO_PIXEL * AMPLIA}px "${FAMILIA_PIXEL}"`
const placas = new Map<string, HTMLCanvasElement>()
function textoEmPixel(texto: string, cor: string): HTMLCanvasElement {
  const chave = `${texto}|${cor}`
  const pronta = placas.get(chave)
  if (pronta) return pronta
  const k = AMPLIA
  const grande = document.createElement('canvas')
  const g = grande.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D
  g.font = FONTE_PIXEL
  const w = Math.ceil(g.measureText(texto).width) + k * 2
  const h = Math.round(TAMANHO_PIXEL * k * 1.25)
  grande.width = w
  grande.height = h
  g.font = FONTE_PIXEL
  g.fillStyle = '#ffffff'
  g.textBaseline = 'alphabetic'
  g.fillText(texto, k, TAMANHO_PIXEL * k)
  const d = g.getImageData(0, 0, w, h).data
  const alfa = (x: number, y: number) => d[(y * w + x) * 4 + 3] ?? 0
  // acha o deslocamento da grade em que cada quadradinho fica todo aceso ou todo apagado
  let melhor = { erro: Infinity, ox: 0, oy: 0 }
  for (let ox = 0; ox < k; ox++)
    for (let oy = 0; oy < k; oy++) {
      let erro = 0
      for (let y = oy; y < h; y += k) for (let x = ox; x < w; x += k) erro += Math.min(alfa(x, y), 255 - alfa(x, y))
      if (erro < melhor.erro) melhor = { erro, ox, oy }
    }
  const W = Math.max(1, Math.floor((w - melhor.ox) / k))
  const H = Math.max(1, Math.floor((h - melhor.oy) / k))
  const aceso = (x: number, y: number) => alfa(melhor.ox + x * k + (k >> 1), melhor.oy + y * k + (k >> 1)) > 127
  // corta o vazio em volta
  let x0 = W
  let x1 = -1
  let y0 = H
  let y1 = -1
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (aceso(x, y)) {
        x0 = Math.min(x0, x)
        x1 = Math.max(x1, x)
        y0 = Math.min(y0, y)
        y1 = Math.max(y1, y)
      }
  const c = document.createElement('canvas')
  if (x1 < 0) {
    c.width = 1
    c.height = 1
    return c
  }
  c.width = x1 - x0 + 1
  c.height = y1 - y0 + 1
  const cg = c.getContext('2d') as CanvasRenderingContext2D
  const img = cg.createImageData(c.width, c.height)
  const n = parseInt(cor.slice(1), 16)
  for (let y = 0; y < c.height; y++)
    for (let x = 0; x < c.width; x++) {
      if (!aceso(x + x0, y + y0)) continue
      const i = (y * c.width + x) * 4
      img.data[i] = (n >> 16) & 255
      img.data[i + 1] = (n >> 8) & 255
      img.data[i + 2] = n & 255
      img.data[i + 3] = 255
    }
  cg.putImageData(img, 0, 0)
  placas.set(chave, c)
  return c
}
const ehCentral = (s: SalaNoMapa) => s.lot === 0

export class Cena {
  private tab: Tabuleiro = tabuleiro('sede')
  private m = medidas(this.tab)
  private alvo = novoAlvo(this.m.largura, this.m.altura, this.m.ox, this.m.oy)
  private lugaresCentrais: Pt[] = []
  private moveis: Movel[] = []
  /** Móvel escolhido no modo de edição: ganha contorno. */
  movelMarcado: string | null = null
  private registro: Registro = { pingue: null, vapores: [], luzes: [], elevadores: [] }
  private readonly base = document.createElement('canvas')
  /** Só os móveis postos por quem administra: aparecem por cima das imagens do Blender. */
  private readonly baseMoveis = document.createElement('canvas')
  private readonly ctx: CanvasRenderingContext2D
  private salas: SalaNoMapa[] = []
  private animados: Animado[] = []
  private quadro = 0
  private ultimo = 0
  private pedido = 0
  private peixes = [
    { x: 0.3, y: 0.4, v: 0.006, c: '#ff8a3d' },
    { x: 0.7, y: 0.65, v: -0.004, c: '#ffd34d' },
    { x: 0.5, y: 0.25, v: 0.003, c: '#ff5d73' },
  ]
  private bolhas = Array.from({ length: 7 }, (_, i) => ({ u: 0.1 + i * 0.12, t: i / 7 }))
  destaque: string | null = null
  /** Visual realista: não pinta o andar em pixel (SPEC-20261010-1914). */
  semFundo = false
  /** Visual realista: as placas são HTML com a fonte do app (no pixel 2.0 continuam em pixel). */
  semPlacas = false
  /** Os móveis postos vêm como imagens do Blender (AndarRealista), não em pixel (SPEC-20261010-2330). */
  moveisPorFora = false
  destaqueNegado: string | null = null
  /** Salas de uma equipe acesas de uma vez, na cor dela, ao chegar pelo portal (SPEC-20261010-1557). */
  equipeAcesa: { salas: Set<string>; cor: string } | null = null
  private readonly parado = matchMedia('(prefers-reduced-motion: reduce)').matches

  private readonly ctxPlacas: CanvasRenderingContext2D | null
  private readonly camadaDePlacas: HTMLCanvasElement | null

  /** `camadaDePlacas`: camada por cima das pessoas, para o nome da sala nunca ficar escondido. */
  constructor(
    private readonly canvas: HTMLCanvasElement,
    camadaDePlacas?: HTMLCanvasElement | null,
  ) {
    this.camadaDePlacas = camadaDePlacas ?? null
    this.ajustarTamanho()
    this.ctx = canvas.getContext('2d') as CanvasRenderingContext2D
    this.ctxPlacas = camadaDePlacas?.getContext('2d') ?? null
    // a placa só sai certa com a fonte carregada; antes disso a de reserva estragaria o cache
    void document.fonts?.load(FONTE_PIXEL).then(() => placas.clear())
  }

  private ajustarTamanho() {
    const { largura, altura } = this.m
    for (const c of [this.canvas, this.base, this.camadaDePlacas]) {
      if (!c) continue
      c.width = largura
      c.height = altura
    }
  }
  get largura() {
    return this.m.largura
  }
  get altura() {
    return this.m.altura
  }
  /** O andar já pintado, sem o que se mexe: serve de miniatura. */
  imagem(): HTMLCanvasElement {
    return this.base
  }
  /** Troca o tabuleiro pronto (Sede, Startup, Coworking, Andar grande). */
  definirTabuleiro(id: string | Tabuleiro) {
    const novo = typeof id === 'string' ? tabuleiro(id) : id
    if (novo === this.tab) return
    this.tab = novo
    this.m = medidas(this.tab)
    this.alvo = novoAlvo(this.m.largura, this.m.altura, this.m.ox, this.m.oy)
    this.ajustarTamanho()
    this.montar()
  }

  /** Móveis postos por quem administra neste tabuleiro. */
  definirMoveis(moveis: Movel[]) {
    const chave = (l: Movel[]) => l.map((m) => `${m.id}:${m.kind}:${m.x}:${m.y}:${m.variant}:${m.color}`).join('|')
    if (chave(moveis) === chave(this.moveis)) return
    this.moveis = moveis
    this.montar()
  }

  definirSalas(salas: SalaNoMapa[]) {
    const chave = (l: SalaNoMapa[]) => l.map((s) => `${s.id}:${s.lot}:${s.template}`).join('|')
    const mudou = chave(salas) !== chave(this.salas)
    this.salas = salas
    if (mudou) this.montar()
  }

  private vagaDe(s: SalaNoMapa): Vaga | null {
    return this.tab.vagas.find((v) => v.id === s.lot) ?? null
  }

  private montar() {
    T = this.alvo
    T.px.fill(0)
    this.animados = []
    const { x0, x1, y0, y1 } = this.tab.predio
    poligono([pr(x0, y0), pr(x1, y0), pr(x1, y1), pr(x0, y1)], PISO)
    // a laje: a caixa do prédio tem frente, nada fica cortado
    paredeY(y1, x0, x1, -9, 0, ruido('#8a93a6', ['#7f879a', '#949db0'], 0.25, 3))
    paredeY(y1, x0, x1, -2, 0, '#5a4128')
    linha(pr(x0, y1, -9), pr(x1, y1, -9), '#3c4253')
    const ocupadas = this.salas.flatMap((s) => {
      const v = ehCentral(s) ? null : this.vagaDe(s)
      return v ? [{ s, v }] : []
    })
    const livre = this.tab.id === 'livre'
    if (!livre) for (const { s, v } of ocupadas) piso(v.x0, v.x1, v.y0, v.y1, 0, MODELOS[s.template].piso[0], MODELOS[s.template].piso[1], Math.round(v.x0 * 13 + v.y0))
    for (const { r, a, b } of this.tab.pisos) piso(r.x0, r.x1, r.y0, r.y1, 0, a, b, 7)
    // vaga sem sala: piso de concreto, só o chão
    for (const v of this.tab.vagas) if (!ocupadas.some((o) => o.v.id === v.id)) chao(v.x0, v.x1, v.y0, v.y1, 0, ruido('#c4c8d1', ['#b8bcc6', '#ced2da'], 0.3, v.id))
    for (const { v } of ocupadas) {
      const y = v.fileira === 'cima' ? v.y1 + 0.3 : v.y0 - 1
      chao(v.x0 + 2.25, v.x0 + 3.75, y, y + 0.7, 0, ruido('#7a5c3c', ['#8a6a48', '#6b4f32'], 0.3, Math.round(v.x0)))
      if (v.fileira === 'cima') sombra([pr(v.x0, v.y1 + 0.3), pr(v.x1, v.y1 + 0.3), pr(v.x1, v.y1 + 0.9), pr(v.x0, v.y1 + 0.9)], 0.12)
    }
    const e = this.tab.entrada
    if (e) chao(e.x0, e.x1, e.y0, e.y1, 0, ruido('#3a3f55', ['#2f3447'], 0.3, 2))
    const itens: Caixa[] = []
    const add: Add = (b, f) => itens.push({ b, f })
    for (const { s, v } of livre ? [] : ocupadas) {
      const vizinha = ocupadas.some((o) => o.v.fileira === v.fileira && Math.abs(o.v.x0 - (v.x1 + 0.3)) < 0.01)
      itensDaSala(add, s.template, v, vizinha)
      this.animados.push({ sala: s, vaga: v })
    }
    REG = { pingue: null, vapores: [], luzes: [], elevadores: [] }
    this.lugaresCentrais = this.tab.montar(add)
    const k = pecas(add)
    for (const m of this.moveis) this.lugaresCentrais.push(...desenharMovel(k, m))
    // planta livre: o lugar de sentar que cai dentro de uma sala é da sala
    if (livre) {
      const soltos: Pt[] = []
      const daSala = new Map<number, Pt[]>()
      for (const a of this.lugaresCentrais) {
        const v = this.tab.vagas.find((v) => v.celulas?.has(Math.floor(a[1]) * (v.w ?? 1) + Math.floor(a[0])))
        if (v) daSala.set(v.id, [...(daSala.get(v.id) ?? []), a])
        else soltos.push(a)
      }
      for (const v of this.tab.vagas) {
        const meio: Pt[] = [...(v.celulas ?? [])].map((c) => [(c % (v.w ?? 1)) + 0.5, Math.floor(c / (v.w ?? 1)) + 0.6] as Pt)
        const cx = (v.x0 + v.x1) / 2
        const cy = (v.y0 + v.y1) / 2
        meio.sort((a, b) => Math.hypot(a[0] - cx, a[1] - cy) - Math.hypot(b[0] - cx, b[1] - cy))
        v.assentos = [...(daSala.get(v.id) ?? []), ...meio]
      }
      this.lugaresCentrais = soltos
    }
    this.registro = REG
    for (const it of ordenar(itens)) it.f()
    ;(this.base.getContext('2d') as CanvasRenderingContext2D).putImageData(T.img, 0, 0)
    // os móveis sozinhos, num desenho à parte, para os visuais feitos no Blender
    this.baseMoveis.width = this.m.largura
    this.baseMoveis.height = this.m.altura
    const gm = this.baseMoveis.getContext('2d') as CanvasRenderingContext2D
    gm.clearRect(0, 0, this.m.largura, this.m.altura)
    if (this.moveis.length) {
      T = novoAlvo(this.m.largura, this.m.altura, this.m.ox, this.m.oy)
      const soMoveis: Caixa[] = []
      const regAntes = REG
      const km = pecas((b, f) => soMoveis.push({ b, f }))
      for (const m of this.moveis) desenharMovel(km, m)
      REG = regAntes
      for (const it of ordenar(soMoveis)) it.f()
      gm.putImageData(T.img, 0, 0)
      T = this.alvo
    }
  }

  /* --------------------------------------------- coordenadas para a tela --- */

  projetar(x: number, y: number, z = 0): Pt {
    return [this.m.ox + x * TX, this.m.oy + y * TY - z]
  }
  /** Onde fica a placa com o nome: em cima da porta (salas) ou acima da marca no chão (área central). */
  etiqueta(s: SalaNoMapa): Pt {
    if (ehCentral(s)) return this.projetar(...this.tab.placaCentral)
    const v = this.vagaDe(s)
    if (!v) return [0, 0]
    return this.projetar((v.x0 + v.x1) / 2, v.y1 + 0.15, v.fileira === 'cima' ? 21 : 20)
  }
  /** Lugar da pessoa i, sentada: em cima de uma cadeira, sofá ou banco. Sem lugar, não aparece. */
  assento(s: SalaNoMapa, i: number): Pt | null {
    if (ehCentral(s)) {
      const a = this.lugaresCentrais[i]
      return a ? this.projetar(a[0], a[1], 5) : null
    }
    const v = this.vagaDe(s)
    if (v?.assentos) {
      const p = v.assentos[i]
      return p ? this.projetar(p[0], p[1], 5) : null
    }
    const a = MODELOS[s.template].assentos[i]
    if (!a || !v) return null
    return this.projetar(v.x0 + a[0], v.y0 + a[1], 5)
  }
  /** Retângulo da sala na tela do tabuleiro, com a altura das paredes: serve para aproximar nela. */
  retanguloDaSala(s: SalaNoMapa): [number, number, number, number] | null {
    const c = this.tab.centrais[0]
    const r = ehCentral(s) ? c : this.vagaDe(s)
    if (!r) return null
    const [a, b] = this.projetar(r.x0, r.y0, ehCentral(s) ? 0 : 24)
    const [x1, y1] = this.projetar(r.x1, r.y1, 0)
    return [a, b, x1, y1]
  }
  /** Ponto do chão sob o ponteiro (em unidades do tabuleiro). */
  chaoEm(X: number, Y: number): Pt {
    return this.noChao(X, Y, 0)
  }
  /** O ponto cai dentro do prédio? */
  dentroDoPredio(x: number, y: number) {
    const p = this.tab.predio
    return x >= p.x0 && x <= p.x1 && y >= p.y0 && y <= p.y1
  }
  /** Móvel sob o ponteiro (o da frente ganha). */
  movelEm(X: number, Y: number): Movel | null {
    for (const z of [0, 5, 10]) {
      const [tx, ty] = this.noChao(X, Y, z)
      const achados = this.moveis.filter((m) => {
        const c = itemDoCatalogo(m.kind)
        return tx >= m.x && tx <= m.x + c.w && ty >= m.y && ty <= m.y + c.d
      })
      if (achados.length) return achados.sort((a, b) => b.y - a.y)[0] ?? null
    }
    return null
  }
  private noChao(X: number, Y: number, z: number): Pt {
    return [(X - this.m.ox) / TX, (Y + z - this.m.oy) / TY]
  }
  salaEm(X: number, Y: number): SalaNoMapa | null {
    for (const z of [0, 6, 12]) {
      const [tx, ty] = this.noChao(X, Y, z)
      const s = this.salas.find((s) => {
        const v = ehCentral(s) ? null : this.vagaDe(s)
        if (v?.celulas) return v.celulas.has(Math.floor(ty) * (v.w ?? 1) + Math.floor(tx))
        return v && tx >= v.x0 - 0.3 && tx < v.x1 + 0.3 && ty >= v.y0 - 0.3 && ty < v.y1 + 0.3
      })
      if (s) return s
    }
    const [tx, ty] = this.noChao(X, Y, 0)
    const dentro = (R: { x0: number; x1: number; y0: number; y1: number }) => tx >= R.x0 && tx < R.x1 && ty >= R.y0 && ty < R.y1
    if (this.tab.centrais.some(dentro)) return this.salas.find(ehCentral) ?? null
    return null
  }

  /* -------------------------------------------------------------- quadros --- */

  iniciar() {
    const laco = (t: number) => {
      if (t - this.ultimo > 83) {
        this.ultimo = t
        if (!this.parado || this.quadro === 0) this.quadro++
        this.desenhar()
      }
      this.pedido = requestAnimationFrame(laco)
    }
    this.pedido = requestAnimationFrame(laco)
  }
  parar() {
    cancelAnimationFrame(this.pedido)
  }

  /** Destaque parado: chão aceso e a sala contornada como uma caixa de luz. */
  private realcar(x0: number, y0: number, x1: number, y1: number, alto: number, cor: string) {
    const g = this.ctx
    const [ax, ay] = pr(x0, y0)
    const [bx, by] = pr(x1, y1)
    g.fillStyle = cor === '#e5484d' ? 'rgba(229,72,77,.16)' : 'rgba(255,176,32,.16)'
    g.fillRect(Math.round(ax), Math.round(ay), Math.round(bx - ax), Math.round(by - ay))
    const caixaDeLuz = (dx: number, dy: number, w: number, estilo: string) => {
      g.strokeStyle = estilo
      g.lineWidth = w
      const r = (x: number, y: number, ww: number, hh: number) => g.strokeRect(Math.round(x) + 0.5 + dx, Math.round(y) + 0.5 + dy, Math.round(ww), Math.round(hh))
      r(ax, ay, bx - ax, by - ay)
      if (alto > 0) {
        r(ax, ay - alto, bx - ax, by - ay)
        g.beginPath()
        for (const x of [ax, bx]) {
          g.moveTo(Math.round(x) + 0.5 + dx, Math.round(by) + 0.5 + dy)
          g.lineTo(Math.round(x) + 0.5 + dx, Math.round(by - alto) + 0.5 + dy)
        }
        g.stroke()
      }
    }
    caixaDeLuz(0, 1, 2, 'rgba(26,31,51,.55)')
    caixaDeLuz(0, 0, 2, cor)
  }

  /**
   * Cada jogada é diferente: lado, altura do arco, ponto da mesa onde quica e
   * velocidade mudam; às vezes sai uma cortada rápida e baixa.
   */
  private bola = { n: 0, t: 0, dur: 18 }
  private bolaDePingue(ponto: (p: Pt, c: string, w?: number, h?: number) => void) {
    const b = this.bola
    if (!this.parado) b.t++
    if (b.t >= b.dur) {
      b.n++
      b.t = 0
      const cortada = hash(b.n, 3, 43) < 0.18
      b.dur = cortada ? 8 + Math.round(hash(b.n, 4, 44) * 3) : 13 + Math.round(hash(b.n, 4, 44) * 12)
    }
    const mesa = this.registro.pingue
    if (!mesa) return
    const [mx, my] = mesa
    const ida = b.n % 2 === 0
    const u = b.t / b.dur
    const x = ida ? mx + 0.2 + u * 3.8 : mx + 4 - u * 3.8
    const y0 = my + 0.3 + hash(b.n, 1, 41) * 1.7
    const y1 = my + 0.3 + hash(b.n + 1, 1, 41) * 1.7
    const y = y0 + (y1 - y0) * u
    const cortada = b.dur <= 11
    const alto = cortada ? 2.5 : 4 + hash(b.n, 2, 42) * 6
    // sobe, desce e quica na metade do adversário (por volta de 70% do caminho)
    const z = u < 0.7 ? 7 + Math.sin((u / 0.7) * Math.PI) * alto : 7 + Math.sin(((u - 0.7) / 0.3) * Math.PI) * alto * 0.45
    const sombra = pr(x, y, 7)
    this.ctx.fillStyle = 'rgba(0,0,0,.25)'
    this.ctx.fillRect(Math.round(sombra[0]), Math.round(sombra[1]), 2, 1)
    ponto(pr(x, y, z), '#ff8a3d', 2, 2)
  }

  private desenhar() {
    T = this.alvo
    const g = this.ctx
    const q = this.quadro
    const ponto = (p: Pt, c: string, w = 1, h = 1) => {
      g.fillStyle = c
      g.fillRect(Math.round(p[0]), Math.round(p[1]), w, h)
    }
    const traco = (a: Pt, b: Pt, c: string) => {
      const n = Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1])) | 0
      for (let s = 0; s <= n; s++) ponto([a[0] + ((b[0] - a[0]) * s) / (n || 1), a[1] + ((b[1] - a[1]) * s) / (n || 1)], c)
    }
    g.clearRect(0, 0, this.m.largura, this.m.altura)
    // no visual realista o andar vem das imagens do Blender, por baixo: aqui só destaque, cadeado e placa
    if (this.semFundo && !this.moveisPorFora) g.drawImage(this.baseMoveis, 0, 0)
    if (!this.semFundo) {
    g.drawImage(this.base, 0, 0)
    // bolinha do pingue-pongue, luminárias, vapor do café e luz do elevador
    this.bolaDePingue(ponto)
    for (const [x, y] of this.registro.luzes) {
      const p = pr(x, y, 21)
      g.fillStyle = `rgba(255,243,196,${0.22 + 0.08 * Math.sin(q * 0.1)})`
      g.fillRect(Math.round(p[0]) - 5, Math.round(p[1]) - 2, 11, 4)
    }
    for (const [vx, vy] of this.registro.vapores)
      for (let k = 0; k < 3; k++) {
        const t = (q * 0.03 + k * 0.33) % 1
        const p = pr(vx, vy, 14 + t * 8)
        g.fillStyle = `rgba(255,255,255,${0.7 * (1 - t)})`
        g.fillRect(Math.round(p[0] + Math.sin(t * 6 + k)), Math.round(p[1]), 1, 1)
      }
    for (const [ex, ey] of this.registro.elevadores) ponto(pr(ex, ey, 20.5), Math.floor(q / 12) % 2 ? '#ffb020' : '#7fd4ff', 3, 1)
    // o que se mexe dentro de cada sala
    for (const A of this.animados) {
      const m = MODELOS[A.sala.template]
      const L = A.vaga
      const W0 = (x: number, y: number, z: number) => pr(L.x0 + x, L.y0 + y, z)
      if (m.anim === 'tv' && L.fileira === 'cima') {
        for (let k = 0; k < 4; k++) {
          const hh = 2 + ((k * 5 + Math.floor(q / 20)) % 7)
          for (let z = 0; z < hh; z++) ponto(pr(L.x0 + 2.3 + k * 0.55, L.y0 - 0.3, 10 + z), ['#6b91f0', '#ffb020', '#7fd4ff', '#b6f08a'][k] as string, 3, 1)
        }
        traco(pr(L.x0 + 2.1, L.y0 - 0.3, 19), pr(L.x0 + 4.5, L.y0 - 0.3, 19), '#ffb020')
      } else if (m.anim === 'codigo') {
        ;([[0.4, 0.6], [3.4, 0.6], [0.4, 3.5], [3.4, 3.5]] as Pt[]).forEach(([x, y], n) => {
          for (let r = 0; r < 3; r++) {
            const len = 0.25 + ((r * 7 + n * 3 + Math.floor(q / 8)) % 5) * 0.1
            traco(W0(x + 0.8, y + 0.3, 10.5 + r * 2), W0(x + 0.8 + len, y + 0.3, 10.5 + r * 2), ['#7fd4ff', '#b6f08a', '#ffd34d'][(r + n) % 3] as string)
          }
        })
        for (let k = 0; k < 5; k++) ponto(W0(5.6, 5.82, 3 + k * 3), hash(k, Math.floor(q / 4), 9) < 0.6 ? '#2fb36d' : '#14532d')
      } else if (m.anim === 'luminaria') {
        const p = W0(4.2, 1.05, 17)
        g.fillStyle = `rgba(255,243,196,${0.25 + 0.1 * Math.sin(q * 0.1)})`
        g.fillRect(Math.round(p[0]) - 4, Math.round(p[1]) + 1, 9, 4)
      } else if (m.anim === 'copa') {
        const aq = (u: number, v: number) => W0(0.4 + u * 3, 1.4, 9.5 + v * 12)
        for (let i = 0; i < 3; i++) {
          const u = (q * 0.004 + i * 0.33) % 1
          traco(aq(u, 0.95), aq(Math.min(1, u + 0.12), 0.95), '#e6f7ff')
        }
        for (const u of [0.15, 0.82])
          for (let k = 0; k < 5; k++) ponto([aq(u, k * 0.07)[0] + Math.round(Math.sin(q * 0.08 + k + u * 9)), aq(u, k * 0.07)[1]], k % 2 ? '#2f8f4e' : '#3fae62', 1, 2)
        for (const f of this.peixes) {
          if (!this.parado) f.x += f.v
          if (f.x > 0.9 || f.x < 0.1) f.v *= -1
          const p = aq(f.x, f.y + Math.sin(q * 0.05 + f.x * 10) * 0.04)
          const x = Math.round(p[0])
          const y = Math.round(p[1])
          g.fillStyle = f.c
          g.fillRect(x - 1, y, 3, 2)
          g.fillStyle = tom(f.c, -0.3)
          g.fillRect(f.v > 0 ? x - 2 : x + 2, y, 1, 2)
          g.fillStyle = '#1a1f33'
          g.fillRect(f.v > 0 ? x + 1 : x - 1, y, 1, 1)
        }
        for (const b of this.bolhas) {
          b.t = (b.t + 0.012) % 1
          ponto(aq(b.u, b.t * 0.95), '#eaf8ff')
        }
        for (let k = 0; k < 4; k++) {
          const t = (q * 0.02 + k * 0.25) % 1
          const p = W0(4.9, 0.7, 18 + t * 12)
          g.fillStyle = `rgba(255,255,255,${0.8 * (1 - t)})`
          g.fillRect(Math.round(p[0] + Math.sin(t * 6 + k) * 1.5), Math.round(p[1]), 1, 1)
        }
      }
    }
    }
    // móvel escolhido no modo de edição
    const mm = this.moveis.find((m) => m.id === this.movelMarcado)
    if (mm) this.realcar(mm.x, mm.y, mm.x + itemDoCatalogo(mm.kind).w, mm.y + itemDoCatalogo(mm.kind).d, 10, '#2fb36d')
    // a equipe que se acabou de abrir: as salas dela piscam na cor da equipe
    if (this.equipeAcesa && this.quadro % 6 < 4) {
      const { salas, cor } = this.equipeAcesa
      for (const s of this.salas) {
        if (!salas.has(s.id)) continue
        const v = ehCentral(s) ? null : this.vagaDe(s)
        if (v) this.realcar(v.x0, v.y0, v.x1, v.y1, 24, cor)
      }
    }
    // destaque da sala sob o mouse (ou negada)
    const marcada = this.salas.find((s) => s.id === (this.destaqueNegado ?? this.destaque))
    if (marcada) {
      const cor = this.destaqueNegado ? '#e5484d' : '#ffb020'
      const c = this.tab.centrais[0]
      if (ehCentral(marcada)) {
        if (c) this.realcar(c.x0, c.y0, c.x1, c.y1, 0, cor)
      }
      else {
        const v = this.vagaDe(marcada)
        if (v) this.realcar(v.x0, v.y0, v.x1, v.y1, 24, cor)
      }
    }
    // cadeados: verde = você entra, amarelo = trancada para você
    for (const s of this.salas) {
      const v = ehCentral(s) ? null : this.vagaDe(s)
      if (!s.trancada || !v) continue
      const p = pr((v.x0 + v.x1) / 2, v.fileira === 'cima' ? v.y1 : v.y0, 19)
      const x = Math.round(p[0])
      const y = Math.round(p[1])
      g.fillStyle = '#1a1f33'
      g.fillRect(x - 4, y - 1, 9, 8)
      g.fillStyle = s.podeEntrar ? '#2fb36d' : '#ffb020'
      g.fillRect(x - 3, y, 7, 6)
      g.fillStyle = '#fff3c4'
      g.fillRect(x - 3, y, 7, 1)
      g.fillStyle = '#1a1f33'
      g.fillRect(x, y + 2, 1, 2)
      g.fillRect(x - 3, y - 5, 1, 4)
      g.fillRect(x + 3, y - (s.podeEntrar ? 7 : 5), 1, s.podeEntrar ? 3 : 4)
      g.fillRect(x - 2, y - 6, 5, 1)
    }
    this.ctxPlacas?.clearRect(0, 0, this.m.largura, this.m.altura)
    // no realista a placa é feita em HTML, com a fonte do app (OfficePage)
    if (!this.semPlacas) for (const s of this.salas) if (s.nome) this.placa(s)
  }

  /** A placa da sala: fundo escuro de cantos cortados, cadeado e a contagem em amarelo. */
  private placa(s: SalaNoMapa) {
    const g = this.ctxPlacas ?? this.ctx
    const sob = s.id === this.destaque || s.id === this.destaqueNegado
    const fundo = sob ? '#ffb020' : s.podeEntrar ? '#1a2140' : '#5b6280'
    const tinta = sob ? '#3a2600' : '#ffffff'
    const texto = textoEmPixel((s.nome ?? '').toUpperCase(), tinta)
    const conta = s.pessoas ? textoEmPixel(String(s.pessoas), '#3a2600') : null
    const cadeado = s.trancada ? 8 : 0
    const h = Math.max(texto.height, 7) + 6
    const w = 4 + cadeado + texto.width + (conta ? conta.width + 9 : 0) + 4
    const [cx, cy] = this.etiqueta(s)
    const x = Math.round(cx - w / 2)
    const y = Math.round(cy - h / 2)
    g.fillStyle = 'rgba(10,14,30,.45)'
    g.fillRect(x + 1, y + h, w - 2, 1)
    g.fillStyle = fundo
    g.fillRect(x + 1, y, w - 2, h)
    g.fillRect(x, y + 1, w, h - 2)
    if (s.cor) {
      g.fillStyle = s.cor
      g.fillRect(x + 1, y + h - 2, w - 2, 2)
    }
    let px = x + 4
    if (cadeado) {
      const ly = y + Math.round(h / 2) - 3
      g.fillStyle = tinta
      g.fillRect(px, ly + 2, 5, 4)
      g.fillRect(px + 1, ly - 1, 1, 3)
      g.fillRect(px + 3, ly - 1, 1, 3)
      g.fillRect(px + 1, ly - 2, 3, 1)
      g.fillStyle = fundo
      g.fillRect(px + 2, ly + 3, 1, 2)
      px += cadeado
    }
    g.drawImage(texto, px, y + Math.round((h - texto.height) / 2))
    px += texto.width + 3
    if (conta) {
      g.fillStyle = sob ? '#fff3c4' : '#ffb020'
      g.fillRect(px + 1, y + 2, conta.width + 4, h - 4)
      g.fillRect(px, y + 3, conta.width + 6, h - 6)
      g.drawImage(conta, px + 3, y + Math.round((h - conta.height) / 2))
    }
  }
}

/** Modelo padrão de cada vaga, o mesmo das salas que o servidor cria. */
const MODELO_DA_VAGA: Record<number, ModeloId> = { 1: 'time', 2: 'reuniao', 3: 'estudio', 4: 'chefia', 5: 'copa', 6: 'cabine', 7: 'cabine', 8: 'estudio', 9: 'reuniao', 10: 'reuniao', 11: 'time', 12: 'cabine' }

/** Miniatura de um tabuleiro pronto, com as salas padrão, para escolher entre eles. */
export function previaDoTabuleiro(id: TabuleiroId): HTMLCanvasElement {
  const cena = new Cena(document.createElement('canvas'))
  cena.definirTabuleiro(id)
  cena.definirSalas(
    tabuleiro(id).vagas.map((v) => ({ id: `previa-${v.id}`, lot: v.id, template: MODELO_DA_VAGA[v.id] ?? 'reuniao', trancada: false, podeEntrar: true })),
  )
  return cena.imagem()
}

/**
 * O desenho simples de um tabuleiro para a planta vista de cima
 * (SPEC-20261010-1557): o contorno do prédio, as vagas de sala, as áreas
 * abertas e a porta de entrada, em unidades do tabuleiro.
 */
export function plantaDoTabuleiro(id: string | null | undefined) {
  const t = tabuleiro(id)
  return {
    predio: t.predio,
    vagas: t.vagas.map((v) => ({ id: v.id, x0: v.x0, y0: v.y0, x1: v.x1, y1: v.y1, fileira: v.fileira })),
    centrais: t.centrais,
    entrada: t.entrada,
  }
}
